<script setup lang="ts">
/**
 * 节点形态 playground
 *
 * 两个节点，把五种行都铺出来：
 *   · 输出点行（端口在右，标签靠右）
 *   · 属性行（只有控件）
 *   · 输入行（左端口 + 控件，连线后控件隐藏）
 *   · 分组行（正则归集 + 折叠，折叠后端口投影到分组行）
 *   · 展示行（高度自定，宿主渲染）
 *
 * 交互：拖空白=平移 · 滚轮=缩放 · 拖节点=移动 · 拖端口=连线
 *       Shift+拖=框选 · Del=删除 · 点分组标题=折叠/展开
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  watchEffect,
} from 'vue'
import type { Component } from 'vue'
/*
 * ⚠️ 2027：画布换成我们自己的（`graph/canvas/`），Vue Flow 全部删掉。
 * 相关的结构类型也从 `graph/flowTypes.ts` 拿 —— 它们本来就是照
 * "我们用到哪几个字段"写的，所以这里只换 import 来源，语义不变。
 */
import NpCanvas from '@/graph/canvas/NpCanvas.vue'
/*
 * ⚠️ 用**别名**导入：这个文件里上千行都在说 `Node` / `Edge` / `NodeChange`，
 * 而它们现在来自我们自己的结构类型。别名让"换来源"只动这一行 ——
 * 顺便避开了 `Node` 撞上 DOM 的全局 `Node`（不别名的话 tsc 会把它当
 * 浏览器的 Node，然后报一堆莫名其妙的"缺少 id / position"）。
 */
import type {
  FlowConnection as Connection,
  FlowEdge as Edge,
  FlowNode as Node,
} from '@/graph/flowTypes'
import type { FlowEdgeChange as EdgeChange, FlowNodeChange as NodeChange } from '@/graph/flowChanges'
import { publishEdgeChanges, publishNodeChanges, republish } from '@/graph/documentSync'
import { splitLevelId } from '@/graph/levelIds'
import {
  loadViewportMemory,
  persistViewportMemory,
  pruneViewportMemory,
  sameViewport,
} from '@/layout/viewportMemory'
import type { Viewport } from '@/layout/viewportMemory'
import NpFlowNode from '@/graph/NpFlowNode.vue'
import { selectedEdgeIdsOf, selectedIdsOf, unwrapArray } from '@/graph/flowStore'
import { viewportOf } from '@/graph/flowStore'
import { edgeIdOf } from '@/graph/edgeOps'
import { FLOW_DOM, HANDLE_ID_ATTR, HANDLE_NODE_ATTR } from '@/graph/graphDom'
import type { FlowStoreApi } from '@/graph/flowStore'
import { initialGestureState, reduce } from '@/graph/canvasGesture'
import type {
  EdgeRef,
  GestureCommand,
  GestureContext,
  GestureEvent,
  GestureState,
  HandleRef,
} from '@/graph/canvasGesture'
import type { CutCandidate, Point } from '@/graph/cutGeometry'
import NpMenu from '@/components/NpMenu.vue'
import NpNodePicker from '@/components/NpNodePicker.vue'
import NpThemeEditor from '@/components/NpThemeEditor.vue'
import { NODE_TYPES, makeModelOfType, nodeIconOf, nodePackageOf, nodeTypeOf } from './nodeTypes'
import {
  GROUP_INPUT_NODE,
  GROUP_OUTPUT_NODE,
  emptyGroupGraph,
  groupInputCount,
  groupRowsFor,
  groupRowsFromGraph,
  planGrouping,
  syncInputNode,
} from '@/node/group'
import { boundsFor, frameFor, moveMembers } from '@/graph/frame'
import type { SubGraph, SubGraphEdge } from '@/types/node'
import {
  DEFAULT_LOOP_SLOTS,
  LOOP_INDEX,
  LOOP_IN_NODE,
  LOOP_ITEM,
  LOOP_OUT_NODE,
  LOOP_PARAMS,
  emptyLoopGraph,
  loopInnerNodes,
  loopRowsFromGraph,
  slotCountOf,
  syncStartNode,
} from '@/node/loop'
import { toDocument } from '@/graph/serialization'
import { fillMissingPositions } from '@/node/subgraph'
import NpLogPanel from '@/components/NpLogPanel.vue'
import NpBoundary from '@/components/NpBoundary.vue'
import { useTouchMenu } from '@/playground/composables/useTouchMenu'
import { useSubgraphLevels } from '@/playground/composables/useSubgraphLevels'
import { useProjection } from '@/playground/composables/useProjection'
import { useExportIr } from '@/playground/composables/useExportIr'
import { useTemplateFile } from '@/playground/composables/useTemplateFile'
import type { TemplateFileCtx } from '@/playground/composables/useTemplateFile'
import { useUndoRedo } from '@/playground/composables/useUndoRedo'
import type { UndoRedoCtx } from '@/playground/composables/useUndoRedo'
import { useClipboard } from '@/playground/composables/useClipboard'
import type { ClipboardCtx } from '@/playground/composables/useClipboard'
import { useNotice } from '@/playground/composables/useNotice'
import { useProblems } from '@/playground/composables/useProblems'
import { useSelection } from '@/playground/composables/useSelection'
import { useSocketTip } from '@/playground/composables/useSocketTip'
import { useAddNode } from '@/playground/composables/useAddNode'
import type { AddNodeCtx } from '@/playground/composables/useAddNode'
import { useConnect } from '@/playground/composables/useConnect'
import { useMenus } from '@/playground/composables/useMenus'
import { useGrouping } from '@/playground/composables/useGrouping'
import type { GroupingCtx } from '@/playground/composables/useGrouping'
import type { MenusCtx, MenuKind } from '@/playground/composables/useMenus'
import { useNodeEditing } from '@/playground/composables/useNodeEditing'
import { useEditorDoc } from '@/playground/composables/useEditorDoc'
import type { EditorDocCtx } from '@/playground/composables/useEditorDoc'
import { useGestureMachine } from '@/playground/composables/useGestureMachine'
import { usePointerTracking } from '@/playground/composables/usePointerTracking'
import { useRepeaterReorder } from '@/playground/composables/useRepeaterReorder'
import { useWidgetEditing } from '@/playground/composables/useWidgetEditing'
import { useCutLasso } from '@/playground/composables/useCutLasso'
import { useNodeIdSeq } from '@/playground/composables/useNodeIdSeq'
import PlaygroundHeader from '@/playground/PlaygroundHeader.vue'
import { useMutedEdgeStyle } from '@/playground/composables/useMutedEdgeStyle'
import { useHotkeys } from '@/playground/composables/useHotkeys'
import { useShortcutActions } from '@/playground/composables/useShortcutActions'
import { usePackages } from '@/playground/composables/usePackages'
import { useRecentNodes } from '@/playground/composables/useRecentNodes'
import { packagesVersion } from '@/playground/nodeTypes'
import { useShortcuts } from '@/playground/composables/useShortcuts'
import { useGestureState } from '@/playground/composables/useGestureState'
import type { MutedEdgeStyleCtx } from '@/playground/composables/useMutedEdgeStyle'
import type { ProjectionCtx } from '@/playground/composables/useProjection'
import type { WidgetEditingCtx } from '@/playground/composables/useWidgetEditing'
import type { RepeaterReorderCtx } from '@/playground/composables/useRepeaterReorder'
import type { GestureMachineCtx } from '@/playground/composables/useGestureMachine'
import type { NodeEditingCtx } from '@/playground/composables/useNodeEditing'
import type {
  EdgeView,
  NodeView,
  SubgraphCtx,
} from '@/playground/composables/useSubgraphLevels'
import type { DocumentEdge, SourceNode } from '@/graph/serialization'
import type { Box, FrameDeleteMode } from '@/graph/frame'
import { alignNodes, canAlign, canResize, resizeNodes } from '@/graph/nodeAlign'
import type { AlignBox, AlignMode, SizeMode } from '@/graph/nodeAlign'
import { pruneMenu } from '@/types/menu'
import type { MenuItem, MenuSection } from '@/types/menu'
import { shortcutLabel } from '@/graph/shortcuts'
import { suppressNativeContextMenu } from '@/layout/contextMenu'
import { MIN_NODE_WIDTH } from '@/theme/tokens'
import { applyTheme, disposeTheme } from '@/theme/applyTheme'
import {
  themeCategoryColor,
  themeSocketColor,
  themeVar,
  useThemeVar,
} from '@/theme/runtime'
import {
  activeThemeId,
  resolvedTheme,
  setActiveTheme,
  themeSummaries,
} from '@/theme/registry'
import { provideRegionTheme } from '@/layout/regionPlacement'
import { EDITOR_META, editorOf, findAreaByEditor, panelTabOf } from '@/layout/areas'
import { areasOf as allAreasOf } from '@/layout/areas'
import { pickTips } from '@/layout/editorTips'
import { EDITOR_ORDER } from '@/layout/areas'
import type { EditorDiagnostic, EditorTip, EditorType, PanelSide } from '@/layout/areas'
import {
  findDuplicateInputs,
  findEdgeOnInput,
  withoutTargetEdge,
} from '@/graph/connectionRules'
import {
  alreadyConnected as checkAlreadyConnected,
  canLink as checkCanLink,
  connectableHandles,
  isStructurallyValid as checkStructurallyValid,
} from '@/graph/linkRules'
import type { LinkCheckInput } from '@/graph/linkRules'
import {
  compactIndexMap,
  wiredRepeaterCount,
  connectedRepeaterCount,
  repeaterHandle,
  repeaterIndexOf,
} from '@/node/rows'
import { debugValuesOf, seedNodesFromRun } from '@/node/debugView'
import { createNodeActions } from './nodeActions'
import type {
  GraphNodeModel,
  GroupRow,
  NodeCategory,
  NodeDiagnostic,
  NodeRow,
  RepeaterRow,
  SocketDef,
} from '@/types/node'
import type { SocketType } from '@/types/socket'
import {
  evaluateGraph,
  formatValue,
  valueKind,
} from '@/node/evaluate'
/*
 * ⚠️ 求值器表从**装配点**来（`@/playground/nodeTypes`），不是从
 * `@/node/evaluators` —— 后者现在只有**内核**求值器，
 * 领域包的求值器由注册表合并进来。宿主拿到的必须是合并后的那一份。
 */
import {
  BUILTIN_EVALUATORS,
  BUILTIN_RUNNERS,
  EXAMPLES,
  exampleOf,
} from '@/playground/nodeTypes'
import {
  JS_NODE_TYPE,
  danglingEdgesAfter,
  handlesOfRows,
  rebuildJsRows,
  type EdgeLike,
} from '@/node/jsNodeType'
import { useRuns } from '@/playground/composables/useRuns'
import { useDocuments } from '@/playground/composables/useDocuments'
import { buildTemplateFile, parseTemplateFile, type TemplateFile } from '@/graph/templateFile'
import type { PackageExample } from '@/node/package'
/* ⚠️ 摘要函数在内核（`node/runGraph.ts`）—— 输出面板也要用它，只有一份 */
import { summarizeRun } from '@/node/runGraph'
import { validateGraph } from '@/node/validate'
import type { ValidateEdge } from '@/node/validate'
import type { WidgetValue } from '@/types/widget'
import NpAreaTree from '@/layout/NpAreaTree.vue'
import PlaygroundProperties from './PlaygroundProperties.vue'
import NpInfoDialog from '@/components/NpInfoDialog.vue'
import NpDocumentManager from '@/components/NpDocumentManager.vue'
import { useAreas } from './composables/useAreas'
import { useMiniMapGesture } from './composables/useMiniMapGesture'
import { useCanvasPinch } from '@/graph/useCanvasPinch'
import { useGraphRegistries } from './composables/useGraphRegistries'
/*
 * ⚠️ 样式从 `<style scoped>` 抽出来了（1072 行，和逻辑无关）。
 * 显式 import 而不是靠 `@import`：Vite 会把它打进产物，
 * 而且**顺序确定**（在下面那个全局 `<style>` 之前）。
 */
import './playground.css'
import {
  closeArea as closeAreaOf,
  countAreas,
  findArea,
  splitArea,
  swapAreas,
} from '@/layout/areas'
import type { RegionKey } from '@/layout/areas'
import type { WindowKey } from './composables/useAreas'

/* ==========================================================================
   ★ 层次地图（script 的组织方式，按依赖方向从下往上读）
   ==========================================================================

   这个文件只做「宿主」该做的事：**持有状态 + 接线 + 视图**。
   业务逻辑在 `composables/`，纯规则在 `graph/` 与 `node/`。

   ① 纯助手 / 数据 ─────────────────────────────────────────────────────
      makeFlowNode / makeEdge
      规矩：**依赖只走参数**。不许从闭包摸响应式状态 —— `makeEdge` 读
      `socketTypeOf` 曾经在接口就绪前被调用而白屏，所以签名改成
      `makeEdge(socketOf, …)`，端口类型由调用方显式传进来。
      ⚠️ 这里原来还有 SAMPLE_DATA / SEED / buildNodes / seedEdges 四个
      **示例**成员（一张 9 节点的顺丰面单演示图）。已删除：内核只提供
      「怎么建节点」，不预置「该有哪些节点」；数据源内容改由 `props.data`
      注入，初始画布为空。

   ② 状态 ──────────────────────────────────────────────────────────────
      nodes / edges / flowStore / activeNodeId / notice / lastNotice /
      windows / light / menu / contextEdgeId / menuPicked / t …
      规矩：**声明归这一层**。需要接口才能算的初值（如 `edges` 的预置数据）
      走「先声明、接口就绪后填充」—— 见 `edges` 声明处的注释。

   ③ 接口与逻辑（composables） ────────────────────────────────────────
      `const { … } = useXxx({ … })`。ctx 里注入的是 ② 的状态和 ① 的助手，
      每个 composable 的依赖一眼可见。**接线本身就是这一层的内容**，
      但接线里出现的每个名字都必须能在 ① / ② 找到出处。

   ④ 宿主事件回调 / 视图 ──────────────────────────────────────────────
      图引擎与 DOM 抛上来的回调、待接预览线、开发期诊断，以及 `<template>`。

   ── 排查问题时先看这里：一个符号属于哪一层，决定了它能不能读别人的东西。

   ★ 层的成员（改动前先对一下这里，别放错）
     ① 纯助手 / 数据：`makeFlowNode` `makeEdge(socketOf, …)` `nextNodeId`
        —— 不许读响应式状态；要什么都走参数
     ② 状态：`nodes` `edges` `flowStore` `activeNodeId` `notice` `lastNotice`
        `windows` `light` `menu` `contextEdgeId` `menuPicked` `t`
        派生：`nodeViews` `modelOf` `currentModels` `edgeViews`
        `looseNodes` `looseNode` `nodePosition` `frameBoxes` `childrenOfFrame`
        `validTargetCount` `statusText` `patternColor` `documentLabel`
     ③ 接口接线：`const { … } = useXxx({ … })`
        —— ctx 里每个名字都要能在 ①② 找到出处（或用闭包转发说明为何不能）
     ④ 宿主回调 / 视图：图引擎与 DOM 的回调、点/框的几何量取
        （`nodeBoxOf` `handleScreenCenter`）、待接预览线、开发期诊断
        + `<template>`
     ⑤ 其它：调参面板、外部可定制信息、菜单 / 建组 / 编辑回调的接线

   ★ 顺序约束只有一个来源：**ctx 是否在 setup 期就求值**。
     回调形式的注入（`doAlign` / `resetGraph` / `makeFlowNode` / `findSocket` /
     `nextNodeId`）**只在事件里被调用**，所以它们不必待在接线之前
     —— 这也是 `doAlign` 能待在 ④ 的原因。
   ========================================================================== */

/* ==========================================================================
   ① 纯助手 / 数据
   ========================================================================== */

interface NpNodeData {
  model: GraphNodeModel
  collapsed: boolean
}


/** 把模型包成图引擎认识的节点 */
function makeFlowNode(
  model: GraphNodeModel,
  position: { x: number; y: number },
): Node {
  /*
   * ★ 框的**包装元素**必须不吃指针事件。
   *
   * 只在内层 `.np-node--frame` 上写 `pointer-events: none` 是不够的 ——
   * 画布的 `.np-graph__node` 包装元素同样覆盖整块区域，而且它默认是
   * `all`，于是框里的节点**点不到**（连线端口也点不到）。
   *
   * 画布把 `node.style` 原样透传给包装元素，而且**我们给的在后** ——
   * 所以这里塞进去的 `pointerEvents: 'none'` 能盖住默认值。
   * 框的标题栏和右下角把手在 CSS 里单独 `pointer-events: auto` 回来。
   */
  const style =
    model.render === 'frame' ? { pointerEvents: 'none' as const } : undefined

  /*
   * ★ **新节点默认在最上面**（`z`）。
   *
   * ⚠️ 只补**没有** `z` 的：老文档里没有这个字段（那时层级是从选中态现算的），
   * 装回来时按数组顺序各给一个号 —— 和它们今天的前后遮挡关系一致。
   * 已有 `z` 的（文档里存过）**原样保留**，不然"存了半天的叠放次序"会在
   * 每次打开时被重排一遍。
   */
  if (model.z === undefined) model.z = nextZ()

  return {
    id: model.id,
    type: 'np',
    position,
    ...(style ? { style } : {}),
    data: { collapsed: false, model },
  }
}


/*
 * 新建节点的 id。计数器被关在 `useNodeIdSeq()` 里 —— 它是这份状态唯一的家，
 * 不再是这个文件里的一个 `let`（之前那种写法让 `nextNodeId` 看着像纯工具，
 * 实际带着跨调用存活的状态，属于隐式依赖）。
 */
const { next: nextNodeId } = useNodeIdSeq()

/*
 * ⚠️ 初始画布**是空的**。
 *
 * 这里原来调 `buildNodes()` 铺一张 9 个节点的演示图（顺丰面单）。
 * 那是**示例**，属于集成方或模板文件的内容，不该长在编辑器内核里 ——
 * 内核只提供「怎么建节点」，不预置「该有哪些节点」。
 * 空画布由 `.pg__empty` 给出可操作的提示（见模板）。
 */
const nodes = ref<Node[]>([])

/* ==========================================================================
   ② 状态与派生
   ==========================================================================
 *
 * 只放「状态」和「从状态派生出来的只读视图」，两者的规矩：
 *   · 状态：`nodes` / `edges` / `flowStore` / `menu` / `t` …
 *   · 派生：`nodeViews` / `modelOf` / `currentModels` / `edgeViews` /
 *     `looseNodes` … `childrenOfFrame` / `validTargetCount` / `statusText`
 *   · **不放命令、不放接线、不放事件回调**
 *
 * 需要接口才能算的初值走「先声明、接口就绪后填充」（见 `edges` 的注释）。
 */

/* ── 端口查询 ───────────────────────────────────────────────── */


function nodeViews(): NodeView[] {
  return nodes.value as unknown as NodeView[]
}

function modelOf(nodeId: string): GraphNodeModel | undefined {
  return nodeViews().find((n) => n.id === nodeId)?.data?.model
}

/* ── 图引擎状态（提前声明：子图层级要用） ────────────────────── */

/** 当前全部节点模型（每次都重新取一份，因为是响应式的） */
function currentModels(): GraphNodeModel[] {
  return nodeViews()
    .map((n) => n.data?.model)
    .filter((m): m is GraphNodeModel => Boolean(m))
}

function edgeViews(): EdgeView[] {
  return edges.value as unknown as EdgeView[]
}

/*
 * ── 节点 / 框的派生视图 ───────────────────────────────────────────
 *
 * 一条链：`nodes` → `looseNodes` → `looseNode` → `nodePosition` /
 * `frameBoxes` / `childrenOfFrame`。
 *
 * 它们都是**状态层的派生**（不碰 DOM、不碰图引擎命令），所以和 `nodes`
 * 放同一层。之前这一段被塞在「框节点」小分区里、中间还隔着 composable 接线，
 * 派生链被打断了。
 * （`nodeBoxOf` 是这条链里唯一**量 DOM** 的，按层次规矩留在宿主回调层。）
 */
type LooseNode = {
  id: string
  position: { x: number; y: number }
  data?: { model?: GraphNodeModel }
}

function looseNodes(): LooseNode[] {
  return nodes.value as unknown as LooseNode[]
}

function looseNode(id: string): LooseNode | undefined {
  return looseNodes().find((n) => n.id === id)
}

function nodePosition(id: string): { x: number; y: number } | null {
  return looseNode(id)?.position ?? null
}

/** 所有框（按数组顺序 = 视觉层次，后画的在上） */
function frameBoxes(): Box[] {
  return looseNodes()
    .filter((n) => n.data?.model?.render === 'frame')
    .map((n) => nodeBoxOf(n.id))
    .filter((b): b is Box => b !== null)
}

function childrenOfFrame(frameId: string): string[] {
  return looseNodes()
    .filter((n) => n.data?.model?.frameId === frameId)
    .map((n) => n.id)
}

/**
 * 当前这一层的边。
 *
 * ⚠️ 这里**只声明、不填充**。预置数据要靠 `socketTypeOf` 算颜色/类型，
 * 而那个是 `useSubgraphLevels` 的产物（下面才解构）—— 在这里就 `seedEdges()`
 * 会 `Cannot access 'socketTypeOf' before initialization`（真炸过一次）。
 *
 * 所以：**声明归状态层，填充归「接口就绪之后」**。这不是妥协 ——
 * 依赖方向本来就是「状态 ← 接口」，层次摆正了，顺序自然是对的。
 */
const edges = ref<Edge[]>([])

/*
 * ══════════════════════════════════════════════════════════════════
 * **每个节点图 Area 一个 VueFlow 实例** —— store 与画布元素都按 Area 索引
 *
 * ⚠️ 这里修的是一个真的抢资源 bug（用户实测）：
 * 「移动端新增窗口的时候，小地图就不能操作了，好像是后创建的节点图窗口
 *   抢了之前的东西（不止小地图，还有选中状态等其他问题）」。
 *
 * 根因：`flowStore` 和 `graphRef` 原来都是**单个** ref。
 *   · `flowStore` 被 `@init` 反复赋值 → **最后初始化的那个实例赢**。
 *     于是「适配画面」「缩放」「小地图拖动」全都打到了另一个窗口上，
 *     而用户手指所在的那个窗口一动不动。
 *   · `graphRef` 绑在 `.np-graph` 上，多个 Area 各有一份 → 同样是最后一个赢。
 *     于是 `sampleEdgePath` / `handleScreenCenter` 去**别的窗口**里查 DOM。
 *
 * 修法：两张 Map，按 Area id 存；对外仍然暴露 `flowStore` / `graphRef`
 * 这两个名字，但它们是**以「焦点 Area」为准的 computed** ——
 * 所有「命令类」操作（fitView / 缩放 / 定位）自然打给当前激活的那个视图。
 *
 * 这正是 Blender 的语义：**每个 Area 有自己的视口**（各自平移缩放），
 * 但底下的数据是同一份；命令作用在**激活的 Area** 上。
 * ══════════════════════════════════════════════════════════════════
 */
/*
 * ══════════════════════════════════════════════════════════════════
 * **文档的唯一写入口**
 *
 * ⚠️ 这两个函数是多窗口方案的地基。为什么不能继续用
 * `v-model:nodes="nodes"`：
 *
 *   1. `v-model` = 一个 ref **两个写者**（两个 VueFlow 都在写它）；
 *   2. 更致命的是 Vue Flow 处理**选中**和**拖动**时是
 *      「就地改对象、返回同一个数组」—— 它自己的
 *      `watch(store.nodes)` 压根不触发，所以**根本不会写回**。
 *      两个窗口于是各自持有一份永不交汇的选中态。
 *
 * 所以改成：`:nodes="nodes"`（单向）+ `@nodes-change` 走这里。
 * `publishNodeChanges` 会**强制换引用**，让另一侧的 prop watcher
 * 真的看到变化、`setNodes` 重新同步（推导详见 `graph/documentSync.ts`）。
 *
 * ⚠️ 不要「顺手」改回 `v-model` —— 那样这两个函数再也不会被调用，
 * 症状是「两个窗口选中不同步 / 旧窗口取消不掉选中 / 拖动另一个窗口不动」。
 * 这些都是真机上出现过的。
 */
/**
 * 「拖动中的位置变更」——这一类**不能**走发布路径。
 *
 * ⚠️ 这是上一轮引入的回归：改成单向 `:nodes` + 每帧强制换引用之后，
 * 拖动会**完全失效**。原因链条：
 *
 *   1. 拖拽由 d3-drag 驱动，它的拖拽主体是 store 里**那一个节点对象**；
 *   2. 我们每帧发布文档 → prop 变化 → 各视图 `setNodes()`；
 *   3. 而 `setNodes` 会 `createGraphNodes()` → 对**每个**节点 `parseNode()`
 *      造对象 → 拖拽主体被换掉；
 *   4. 下一步 pointermove 再去更新那个已经不在 store 里的旧对象，
 *      画布上就再也不动了。
 *
 * 所以位置变更改成**就地写**：文档和各视图的节点对象都直接改 `position`。
 * 这些对象是 `reactive(state)` 里的深层响应式对象（Vue Flow 的 store 是
 * `reactive(state)`，不是 shallowRef），所以就地改**能**让对应视图重渲染 ——
 * 既拿到了跨窗口实时同步，又不重建数组、不打断拖拽。
 */
function isDraggingPositionChange(change: NodeChange): boolean {
  const c = change as unknown as { type?: string; dragging?: boolean; position?: unknown }
  return c.type === 'position' && Boolean(c.dragging) && Boolean(c.position)
}

/** 把一批位置就地写进某个节点数组（找不到的 id 跳过） */
function writePositions(
  list: unknown,
  positions: Map<string, { x: number; y: number }>,
) {
  if (!Array.isArray(list)) return
  for (const node of list as { id?: string; position?: { x: number; y: number } }[]) {
    const next = node.id ? positions.get(node.id) : undefined
    if (next && node.position) node.position = { x: next.x, y: next.y }
  }
}

function onNodesChange(changes: NodeChange[]) {
  /*
   * 只要这批变更里**有**拖动中的位置变更，就整批走就地路径、**不发布**：
   * 发布一定会换引用 → `setNodes` → 拖拽主体被换掉。
   * 这批里其它类型的变更（比如「拖一个没选中的节点顺带把它选中」）
   * 图引擎自己已经就地应用过了（`applyDefault`），等松手时
   * `publishFromStore()` 会连同它们一起发布出去，不会丢。
   */
  if (changes.some(isDraggingPositionChange)) {
    const positions = new Map<string, { x: number; y: number }>()
    for (const change of changes) {
      if (!isDraggingPositionChange(change)) continue
      const c = change as unknown as { id: string; position: { x: number; y: number } }
      positions.set(c.id, c.position)
    }
    writePositions(nodes.value, positions)
    for (const store of flowStores.values()) {
      writePositions((store as { getNodes?: { value?: unknown } }).getNodes?.value, positions)
    }
    return
  }

  /* ⚠️ `as unknown as Node[]`：Vue Flow 的 Node 泛型太深（TS2589），本文件统一这么转 */
  nodes.value = publishNodeChanges(
    changes,
    nodes.value as unknown as Parameters<typeof publishNodeChanges>[1],
  ) as unknown as Node[]
}

/**
 * 拖动结束：以**这个视图的 store** 为权威，整份发布一次。
 *
 * 拖动期间位置是就地写的（没走发布），松手后必须收敛一次，
 * 否则文档里可能缺了拖动途中顺带发生的其它变更（选中之类），
 * 下一次别的发布就会把它们盖掉。
 *
 * 从 store 取而不是从文档取，是因为**这一份**才是刚刚被操作过的、
 * 信息最全的那一份。
 */
function publishFromStore(areaId: string) {
  const live = (flowStores.get(areaId) as { getNodes?: { value?: unknown } } | undefined)
    ?.getNodes?.value
  if (!Array.isArray(live) || live.length === 0) return
  nodes.value = republish(
    live as unknown as { id: string }[],
    null,
  ) as unknown as Node[]
}

/**
 * 把一批节点的新位置写进文档（拖框时子节点跟着走）。
 *
 * ⚠️ 必须走这里而不是就地改 store 里的节点对象 —— 理由见
 * `useGrouping` 里 `applyNodePositions` 的注释（就地改会被下一帧
 * 的文档发布按旧值盖回去，表现是拖框时子节点来回跳）。
 */
function applyNodePositions(moves: { id: string; x: number; y: number }[]) {
  if (moves.length === 0) return
  const byId = new Map(moves.map((m) => [m.id, m]))
  /* 同上：Node 泛型太深，转一手 */
  const list = nodes.value as unknown as { id: string }[]
  nodes.value = list.map((node) => {
    const move = byId.get(node.id)
    return move ? { ...node, position: { x: move.x, y: move.y } } : node
  }) as unknown as Node[]
}

function onEdgesChange(changes: EdgeChange[]) {
  edges.value = publishEdgeChanges(
    changes,
    edges.value as unknown as Parameters<typeof publishEdgeChanges>[1],
  ) as unknown as Edge[]
}

/* ⚠️ `flowStores` / `graphEls` 已归 `useMiniMapGesture`（只有它和下面
 * 的 Area 挂载回调在用）—— 这里只是解构出来。*/

/**
 * 手动触发重算的计数器。
 *
 * ⚠️ `Map` 不是响应式的 —— 往里面写东西不会让 computed 失效。
 * 所以每次注册 / 注销实例都 `+= 1`，computed 里读一下它建立依赖。
 * （不用 `reactive(Map)` 是因为这里只需要「变了」这一个信号，
 *  不需要逐项追踪。）
 */
const storesTick = ref(0)

/*
 * ── 每个 Area 的视口记忆（平移 / 缩放）──────────────────────────
 *
 * Vue Flow 的 store 按 Area id 建。正常切 Area 只是 `v-show` 隐藏、不卸载，
 * 所以视口天然还在；但**交换两个 Area 的内容**、换编辑器再换回来、
 * 刷新页面，都会重建 store —— 不记住的话表现是「我调好的视角没了」，
 * 而用户完全没做任何和视角有关的事。
 *
 * ⚠️ 按 **Area id** 记，不按编辑器：「节点图」可以同时出现在两个 Area 上
 * （多窗口），它们**各自**有自己的视口，按编辑器记会让两个窗口互相打架。
 */
const viewportStorage = typeof localStorage === 'undefined' ? null : localStorage
const areaViewports = ref<Record<string, Viewport>>(loadViewportMemory(viewportStorage))

/*
 * 每个 Area 里滚动容器的位置。
 *
 * ⚠️ **只在内存里**，不落盘（对比视口是落盘的）。理由：滚动位置和
 * 「我刚才在翻什么」强相关，隔一次刷新再恢复出来通常是**意外**而不是帮助
 * （用户已经不记得自己上次停在哪了）。而视口是「我搭好的视角」，
 * 跨会话恢复是有意义的。两者语义不同，所以处理方式也不同。
 */
const areaScrolls = ref<Record<string, { selector: string; top: number; left: number }>>({})

function rememberScroll(areaId: string, mark: { selector: string; top: number; left: number }) {
  areaScrolls.value = { ...areaScrolls.value, [areaId]: mark }
}

/** 某个 Area 的视口变了 —— 记下来（同值不重复写盘） */
function rememberViewport(areaId: string, vp: unknown) {
  const next = vp as Viewport
  if (!next || !Number.isFinite(next.x) || !Number.isFinite(next.y) || !(next.zoom > 0)) return
  if (sameViewport(areaViewports.value[areaId], next)) return
  areaViewports.value = { ...areaViewports.value, [areaId]: { x: next.x, y: next.y, zoom: next.zoom } }
  persistViewportMemory(viewportStorage, areaViewports.value)
}

/** 当前**焦点** Area 的 store —— 命令类操作打给它 */
const flowStore = computed<FlowStoreApi | null>(() => {
  void storesTick.value
  return flowStores.get(focusedId.value) ?? null
})
const activeNodeId = ref<string | null>(null)

/** 列表元素类型推导成功的临时提示 */
const notice = ref('')
/**
 * 最后一条设计器提示。
 *
 * 提示条几秒就没，但用户常常需要回头确认「刚才到底做了什么」——
 * 所以这里留一份，显示在输出窗口里（只留**最新一条**，不攒历史）。
 */
const lastNotice = ref('')

/*
 * ── 状态栏提示条（逻辑在 composables/useNotice.ts）────────────────
 *
 * ⚠️ 这是 ② 区里**唯一**的 composable 调用，而且不能挪进 ③ 区：
 * `useSubgraphLevels` 要注入 `showNotice`，而 composable 初始化是立即执行的
 * —— 放在它后面就是 `TS2448` / 运行期读到 undefined。
 *
 * 结论：**「必须早于接线」的初始化留在 ② 区**，并在注释里写明原因。
 * 它只依赖 `notice` / `lastNotice` 两个状态，放在这里也不破坏层次。
 */
const { noticeActions, clearNotice, runNoticeAction, showNotice } = useNotice({
  notice,
  lastNotice,
})


/* ── 视图开关 / 菜单状态 / 画布指针位置（提前声明：接线要读） ──── */

/**
 * 触摸端的「框选」模式开关。
 *
 * ## 为什么必须是个模式，而不是一个手势
 *
 * Vue Flow 里**单指平移**和**单指框选**由同一个值 `panOnDrag` 决定：
 *
 *   `panOnDrag = true`   → 单指平移 ✅、双指捏合缩放 ✅、框选 ❌
 *   `panOnDrag = false` + `selectionKeyCode = true` → 单指框选 ✅、缩放 ❌
 *
 * 触摸端没有修饰键可用，所以两者**不可能同时成立**。
 * 桌面端不受影响（左键框选 / 中键平移 / 空格临时平移都还在）。
 *
 * 于是做成开关：默认「平移 + 缩放」，需要框选时按一下工具条上的按钮。
 */

/*
 * 「添加节点」和「添加资产」是**两类弹层**，但共用同一套判据：
 * 触摸端走全屏选择器，桌面端走下拉菜单。
 *
 * ⚠️ 抽成 computed 而不是写在模板里：模板里的复合条件会让
 * vue-tsc 对 `menu.kind` 做（不准确的）控制流收窄，
 * 于是 `menu.kind === 'asset'` 被报成「没有重叠」。
 * 抽出来之后模板只看到布尔，两边都干净。
 */
const pickerKinds: readonly string[] = ['add', 'asset']

/** 搜索框的提示语 —— 也抽出来，理由同上（模板里别对 `menu.kind` 做比较） */
const searchPlaceholder = computed(() => {
  const kind = menu.value?.kind
  if (kind === 'link') return '搜索要接的节点…'
  if (kind === 'asset') return '搜索资产…'
  return '搜索节点类型…'
})
const showNodePicker = computed(
  () => Boolean(menu.value) && pickerKinds.includes(menu.value!.kind) && touchDevice.value,
)

/**
 * 小地图尺寸 —— **必须由 props 驱动**（见模板里的注释）：
 * MiniMap 读 `props.width/height`，不读 CSS。手指比鼠标粗，触摸端放大一档。
 */
/*
 * ⚠️ 这里原来有一个 `windowToggles` computed —— 「小地图 / 输出 / 配置行 / 调参」
 * 四个窗口开关的图标 + 状态 + 切换动作，渲染成工具栏中段的一排按钮。
 *
 * **整块删除**：那四个开关在「窗口」菜单里已经有一份，
 * 每个 Area 的 header 里也有一份（按 Area 而不是全局）——
 * 三份 UI 写同一份状态，是 §P5 的典型。现在窗口开关归
 * 「窗口」菜单 + Area header，工具栏不再掺和。
 *
 * `toggleWindow` / `windows` 仍然保留（菜单和底栏在用）。
 */

/*
 * 小地图尺寸。
 *
 * ⚠️ 触摸端要**小得多**：390px 宽的屏上 200px 的小地图占掉一半以上宽度，
 * 而且它压在画布右下角、正好是拇指够得着又容易误碰的地方
 * （用户反馈：「小地图在移动端是不是有点大了」）。
 * 桌面端保持 200×130 —— 那里它是真的有用的一块信息。
 *
 * 宽高比固定 200:130，缩小时不会把图画扁。
 */
const MINIMAP_DESKTOP = { width: 200, height: 130 } as const
const MINIMAP_TOUCH = { width: 124, height: 81 } as const
/**
 * 小地图尺寸。
 *
 * ⚠️ 主题可以覆盖（`layout.canvas.minimapWidth/Height`）。
 * 触摸端本来就有一套更小的尺寸 —— 那是**内置默认**，主题优先。
 */
const minimapSize = computed(() => {
  const c = resolvedTheme.value.layout.canvas
  if (c?.minimapWidth && c?.minimapHeight) {
    return { width: c.minimapWidth, height: c.minimapHeight }
  }
  return touchDevice.value ? MINIMAP_TOUCH : MINIMAP_DESKTOP
})

/**
 * 小地图贴哪个角。
 *
 * ⚠️ 它同时决定折叠按钮的位置 —— 两者错开会得到
 * 「按钮飘在半空、小地图在另一头」，所以它们必须读**同一个**来源。
 * 折叠按钮的偏移写在 CSS 里，靠的是同一个 `data-np-minimap-corner`。
 */
const minimapCorner = computed(() => resolvedTheme.value.layout.canvas?.minimapCorner ?? 'br')

const minimapPosition = computed(() => {
  const map: Record<string, string> = {
    tl: 'top-left',
    tr: 'top-right',
    bl: 'bottom-left',
    br: 'bottom-right',
  }
  return map[minimapCorner.value] ?? 'bottom-right'
})

/**
 * 小地图折起来了吗。
 *
 * ⚠️ 这是**视图内的**折叠，和「窗口 → 小地图」那个 Region 开关是两件事：
 *   · Region 开关 = 这个窗口**没有**小地图（省的是渲染）；
 *   · 折叠 = 小地图**在但收起来了**（省的是画面，随手就能展开）。
 * 用户要的是后者：「能不能给小地图加个折叠功能，这样可以收起来」。
 *
 * 只记在内存里、不写进布局文档：布局文档描述的是**窗口结构**，
 * 折叠是「这个角落现在挡不挡事」的临时状态。
 */
/*
 * 小地图折叠状态 —— **按 Area 分别记**。
 *
 * ⚠️ 原来是一个全局布尔，于是「在 A 窗口折叠，B 窗口也跟着折叠了」。
 * 折叠是「这个角落现在挡不挡事」的**局部**判断：两个窗口大小不同、
 * 看的区域不同，该不该收起来本来就可能不一样。
 * 用 Area id 做键，和视口记忆同一套思路。
 */
const minimapFolded = ref<Record<string, boolean>>({})

function isMinimapFolded(areaId: string): boolean {
  return Boolean(minimapFolded.value[areaId])
}

function toggleMinimapFold(areaId: string) {
  minimapFolded.value = { ...minimapFolded.value, [areaId]: !minimapFolded.value[areaId] }
}

/**
 * 小地图的遮罩色（视口之外的区域）。
 *
 * ⚠️ 原来写死 `rgba(0, 0, 0, 0.55)` —— 浅色主题下那块会一直是深色。
 * 小地图在手机上很显眼（右下角固定），所以这条在移动端最容易被看见，
 * 表现就是用户说的「移动端浅色主题没有正确应用」。
 */
const minimapMaskColor = useThemeVar('--np-minimap-mask', 'rgba(0, 0, 0, 0.55)')

/**
 * 把 JS 侧的几何真值送进 CSS。
 *
 * ⚠️ 为什么反过来（JS → CSS）而不是让 JS 去读 CSS：
 * MiniMap 的 `:width` / `:height` 是 **props**，它压根不读 CSS 的
 * width/height（踩过：用 class 设 200×130，SVG 仍按默认 200×150 画，
 * 于是右侧多一块、点击坐标换算也按 150 算 →「拖不动」）。
 * 所以真值只能在 JS 里，CSS 必须来读它。
 */
const cssVars = computed(() => ({
  '--np-minimap-w': `${minimapSize.value.width}px`,
  '--np-minimap-h': `${minimapSize.value.height}px`,
}))



/*
 * ── 主题 ───────────────────────────────────────────────────────
 *
 * ⚠️ 主题要**记住**。它是一个明确的用户选择，而默认值是深色 ——
 * 不落盘的话每次刷新都回到深色，用户会以为「切了没用」。
 * 真机上这一点尤其明显：手机处处会重载页面。
 *
 * ⚠️ 我们**不跟随系统**的 `prefers-color-scheme`。用户明确说过
 * 「手机系统开了深色模式，页面上一直是深色」—— 那是浏览器对未声明
 * `color-scheme` 的页面做「强制深色」的结果（退出信号见 theme.css 的
 * `color-scheme: only light`）。这里只管「用户在这个界面里选了什么」。
 *
 * ## 从「一个布尔」到「一份主题文档」
 *
 * 改动前这里只有 `const light = ref(...)` + 一个类名开关，整套观感
 * 靠 `theme.css` 里第二份硬编码的浅色值。现在：
 *
 * ```text
 *   theme/registry.ts   主题注册表（内置 + 用户 + 当前选中），落 localStorage
 *   theme/applyTheme.ts 把解析结果写成内联变量 + data-* 属性 + <style>
 *   theme/runtime.ts    让 JS 侧（连线 / 小地图）读回解析后的值
 * ```
 *
 * `light` 保留成**派生值**：它仍然是 `scheme === 'light'`，
 * 类名也照挂 —— 因为 `playground.css` / `np-menu.css` 里还有一批
 * 旧的 `.np-theme-light` 覆盖在用。主题的内联变量优先级更高，
 * 所以两者共存时主题说了算，旧规则只是「主题没管到的地方」的兜底。
 */
const designerEl = ref<HTMLElement | null>(null)

/**
 * 右键：窗口区域内不弹浏览器菜单（可编辑控件除外 —— 见模板上的说明）。
 *
 * ⚠️ 只 `preventDefault()`，**不** `stopPropagation()`：
 * 节点图 / 窗口头 / 画布自己的右键菜单都挂在更里层，它们照常收到事件。
 */
function onContextMenu(ev: MouseEvent): void {
  if (suppressNativeContextMenu(ev.target)) ev.preventDefault()
}

/** 当前主题是不是浅色底。派生自主题文档，不再是独立状态 */
const light = computed(() => resolvedTheme.value.scheme === 'light')

/** 当前主题的摘要 —— 菜单项的副标题要用 */
const activeThemeSummary = computed(() =>
  themeSummaries.value.find((t) => t.id === activeThemeId.value),
)

/**
 * 指针悬停在哪个 Area 上（`null` = 没有指针，比如触摸端）。
 *
 * ⚠️ 它只影响**底部提示条显示谁**，不影响焦点 —— 「激活」和「鼠标在哪」
 * 在 Blender 里也是两件事：前者决定快捷键发给谁，后者决定提示说什么。
 */
const hoveredAreaId = ref<string | null>(null)

/**
 * 信息弹窗开着哪一个（`null` = 没开）。
 *
 * ⚠️ 帮助 / 关于**不用提示条**：提示条是单行 + 横向滚动的，
 * 而帮助要列七八条快捷键 —— 得横着划才看得全，而且几秒后淡出，
 * 想照着敲第二个键时已经没了。
 * 分工是：**一次性反馈**走提示条，**要读、要照着做**的走弹窗。
 */
const infoDialog = ref<'about' | 'help' | null>(null)

function openInfo(which: 'about' | 'help'): void {
  infoDialog.value = which
}

/** 「关于」里那几行状态 —— 都是当前真实值，不写死 */
/*
 * ⚠️ 「关于」里那句"这是什么"**不许写死某个包**：这个编辑器是**通用的节点式
 * 设计器**，打印只是**装了一个包**之后的形态。真机反馈："关于里边却说这是一个
 * 节点式打印编辑器"—— 换个包（数据源 / 图像 / 以后别的域）它就不是打印机了。
 */
const ABOUT_INTRO =
  '节点式可视化设计器：装了什么节点包，它就长成什么样子 —— 换一个包就换一套窗口、节点与操作方式。'

/**
 * 「关于」里那行"装了哪些包" —— 从**注册表**读，不写死。
 *
 * ⚠️ 必须等 `installedPackages` 声明之后才能算（它是 computed，但**读的时候**
 * 要是最新的那一份），所以用闭包取值的写法在这里不适用 —— 直接依赖它即可。
 */
const enabledPackagesSummary = computed(() => {
  const list = installedPackages.value.filter((pkg) => pkg.enabled).map((pkg) => pkg.label)
  return list.length ? list.join(' · ') : '（没有启用任何节点包）'
})

const aboutRows = computed(() => [
  { label: '版本', value: props.editorVersion || '（未提供）' },
  { label: '主题', value: `${activeThemeSummary.value?.name ?? activeThemeId.value}（${resolvedTheme.value.scheme === 'light' ? '浅色' : '深色'}）` },
  { label: '工作区', value: activeWorkspaceId.value ?? '自定义' },
  { label: '窗口', value: `${areaIds.value.length} 个` },
  { label: '节点', value: `${nodes.value.length} 个 · ${edges.value.length} 条连线` },
])

/**
 * 「帮助」里的快捷键与手势。
 *
 * ⚠️ 这份清单和底部提示条**不是同一份数据**：提示条列的是
 * 「这一屏、这个窗口当下能用的几个键」（由 `visibleShortcuts` 按上下文算），
 * 而帮助要的是**全量、分类、带说明**的参考表。硬凑成一份会让两边都变差
 * （提示条被撑长，帮助里混进当下用不上的键）。
 */
/**
 * 「帮助」里**从注册表自动生成**的那一段：**每个窗口自己的操作方式**。
 *
 * ⚠️ 这一段的来源是 `EDITOR_META[...].tips` —— 和底部提示条**同一份数据**。
 * 好处是"包贡献了窗口，帮助里就自动有它的操作方式"：不用改宿主、不会漏、
 * 也不会写在两处之后漂移（真机反馈问的正是这件事："增加了这种域节点包的时候，
 * 相关的操作要不要也自动加载到帮助里？" —— 要，而且只能这么要）。
 *
 * ⚠️ 桌面端与触摸端分开列：`touchKeys` 那套是手机上真实可用的操作，
 * 混在一行里会让人以为手机也能按 Del。
 */
const helpEditorRows = computed(() =>
  EDITOR_ORDER.filter((editor) => (EDITOR_META[editor].tips?.length ?? 0) > 0).map((editor) => ({
    group: EDITOR_META[editor].label,
    /*
     * ⚠️ 帮助里**两种设备都说**（它就是"全量参考表"）：桌面那套 + 触摸那套
     * 各列一行，而不是按当前设备过滤 —— 否则在手机上就查不到"电脑上怎么用"。
     * 判定仍然来自同一个 `pickTips`（不写第二份规则）。
     */
    items: [
      ...pickTips(EDITOR_META[editor].tips, false).map((tip) => ({
        keys: tip.keys,
        what: tip.label,
      })),
      ...pickTips(EDITOR_META[editor].tips, true)
        .filter((tip) => !EDITOR_META[editor].tips!.some((raw) => raw.keys === tip.keys))
        .map((tip) => ({ keys: `触摸端：${tip.keys}`, what: tip.label })),
    ],
  })),
)

/** 帮助里的全部段落：手写的通用部分 + 从注册表生成的那部分 */
const helpRows = computed(() => [...HELP_ROWS, ...helpEditorRows.value])

const HELP_ROWS: readonly { group: string; items: { keys: string; what: string }[] }[] = [
  {
    group: '画布',
    items: [
      { keys: '拖动 / 单指拖动', what: '平移画布（手模式）' },
      { keys: '滚轮 / 双指捏合', what: '缩放' },
      { keys: '双击空白 / Shift+A', what: '添加节点' },
      { keys: '右键空白 / 长按空白', what: '画布菜单' },
      { keys: 'Ctrl+A', what: '全选' },
    ],
  },
  {
    group: '节点',
    items: [
      { keys: '右键节点 / 长按节点', what: '节点菜单' },
      { keys: '双击标题', what: '改名字' },
      { keys: 'Delete', what: '删除选中' },
      { keys: 'Ctrl+G', what: '打成组' },
      { keys: 'Ctrl+H', what: '框住选中（建帧）' },
      { keys: '从端口拖出', what: '连线' },
      { keys: 'Ctrl+拖', what: '剪线' },
    ],
  },
  {
    group: '窗口',
    items: [
      { keys: '左上角图标', what: '换窗口类型' },
      { keys: 'Alt + 1…9', what: '把**焦点窗口**切成第 N 种窗口类型' },
      { keys: '拖动窗口之间的缝', what: '改分屏比例；拖过头 = 合并' },
      { keys: '右键窗口标题栏', what: '分屏 / 最大化 / 关闭 / 侧栏' },
      { keys: '头部那条可以横滚', what: '鼠标滚轮或手指横滑看后面的按钮' },
      { keys: '点一下分割把手', what: '左右分屏；按住拖 = 按方向分（上下 / 左右）' },
      { keys: 'header 上的手按钮', what: '亮 = 移动，灭 = 框选' },
    ],
  },
]

/** 提示条显示谁的提示：指针优先，没有就用焦点 */
const hintArea = computed(() => {
  const id = hoveredAreaId.value ?? focusedId.value
  return findArea(layout.value.root, id)
})

/**
 * 指针进入某个 Area。
 *
 * ⚠️ 用 `closest` 反查而不是给每个 Area 加事件：事件本来就冒泡到容器，
 * 而 `.np-area` 上已经有 `data-area-id`（菜单和验证脚本也在用它）。
 * 给递归的 `NpAreaTree` 多加一个透传事件，对中间层毫无意义。
 */
function onAreaPointerOver(ev: PointerEvent): void {
  const el = (ev.target as HTMLElement | null)?.closest?.('.np-area[data-area-id]')
  const id = el?.getAttribute('data-area-id') ?? null
  if (id !== hoveredAreaId.value) hoveredAreaId.value = id
}


/*
 * 把主题的**布局声明**注入 Area 树。
 *
 * ⚠️ 走 provide/inject 而不是逐层传 prop：`NpAreaTree` 是递归的，
 * 而那个参数对它自己毫无意义（见 `regionPlacement.ts` 的注释）。
 * ⚠️ 也不能让 `layout/` 直接读主题注册表 —— 分层守卫不许。
 */
provideRegionTheme(computed(() => resolvedTheme.value.layout))

/**
 * 全局顶栏显不显示。
 *
 * ⚠️ 主题可以把它关掉（沉浸模式：整屏都是画布）。
 * 但**必须留一条回来的路** —— 关掉之后「视图」菜单也跟着没了，
 * 用户就再也打不开它。所以关掉时在画布角上留一个「⋯」按钮。
 */
const chromeHeaderVisible = computed(
  () => resolvedTheme.value.layout.chrome?.header?.visible !== false,
)

/**
 * 应用主题。
 *
 * ⚠️ 两处都要写变量（`<html>` 与设计器根节点），理由见 `applyTheme.ts` 头部：
 * CSS 自定义属性虽然继承，但**元素自己声明的会盖掉继承值**，
 * 而 `theme.css` 在 `.np-designer` 上声明了一整块令牌。
 * 只写 `<html>` 的话主题会在这里**静默失效**。
 *
 * ⚠️ `flush: 'post'`：等 DOM 更新完再应用，否则首次挂载时
 * `designerEl` 还是 `null`，那次主题就只写到了 `<html>` 上。
 */
watch(
  [resolvedTheme, designerEl],
  ([theme, el]) => {
    if (typeof document === 'undefined') return
    applyTheme(theme, { root: document.documentElement, scope: el })
    /*
     * 兼容层：`.np-theme-light` 类照挂。
     * 它现在只承担「主题没显式覆盖的那些令牌」的兜底。
     */
    document.documentElement.classList.toggle('np-theme-light', theme.scheme === 'light')
  },
  { immediate: true, flush: 'post' },
)

onBeforeUnmount(() => {
  disposeTheme()
  document.documentElement.classList.remove('np-theme-light')
})

/* ── 窗口 / Area 布局 ─────────────────────────────────────────
 *
 * ⚠️ 这里**不再**是 `ref({ minimap, problems, keys, theme })` 那几个布尔。
 *
 * 那五个布尔是「没有几何的状态」：没有位置、没有归属、没有嵌套 ——
 * 于是每个窗口在哪儿只能靠 CSS 手算（`.pg__side { bottom: 158px }` 那一类），
 * 而「切分屏幕」这件事根本没有表达方式。
 *
 * 现在真值是 `src/layout/areas.ts` 里的一棵 **Area 树**，`windows` 只是
 * 从它派生出来的**只读兼容视图**（`useAreas`）。旧代码读 `windows.value.x`
 * 一个字不用改；写入必须走 `setWindow` / `toggleWindow`。
 *
 * ★ 特意保留这五个名字（而不是一次性全改成 area 概念）：
 *   它们是「用户嘴里的窗口名」，而且菜单文本、工具栏 aria-label 都在用。
 *   一次改到底会把这一步变成不可回滚的大爆炸，而兼容层让第 7 步
 *   （把渲染层换成 NpAreaTree）可以**单独**做、单独验。
 */
const {
  layout,
  windows,
  graphAreaId,
  areaIds,
  maximizedId,
  focusedId,
  setWindow,
  toggleWindow,
  split,
  close: closeAreaById,
  setEditor,
  setRatio,
  toggleRegion,
  setRegion,
  setPanelOfSide,
  focus: focusAreaById,
  maximize,
  workspaces,
  activeWorkspaceId,
  applyWorkspace,
  saveWorkspace,
  deleteWorkspace,
  load: loadLayout,
  loadFromStorage: loadLayoutFromStorage,
  saveToStorage: saveLayoutToStorage,
  reveal: revealEditorWindow,
} = useAreas()

/*
 * ★ 小地图的触摸手势 + 两个 Area 注册表（`flowStores` / `graphEls`）
 * —— 从本文件抽到 `composables/useMiniMapGesture.ts`（125 行）。
 *
 * ⚠️ **必须在这里调用**：`flowStores` 在下面几个函数体里就被引用了
 * （`publishNodeChanges` / `publishFromStore`），而它们是运行期才执行的，
 * 所以只要**声明在它们之前**就够 —— 而 `focusedId` 又来自上面刚解构的
 * `useAreas()`。三个条件一起把位置钉在了这儿。
 *
 * ⚠️ 入参传的是 **getter**（`() => x.value`）：
 * `spaceHeld` / `touchDevice` 的声明都比这里晚（`touchDevice` 来自
 * `useHotkeys`，更晚），直接传 ref 会撞 TDZ。这是本仓库处理声明顺序的老规矩。
 */
/*
 * ★ 两个 Area 注册表（`flowStores` / `graphEls`）**归 `useGraphRegistries`**。
 *
 * ⚠️ 它们原来被放在 `useMiniMapGesture` 里 —— 那是我上一轮抽小地图时
 * **顺手放错的**：小地图只是 7 个消费者之一，这两个 Map 的真身是
 * 「Area → 图引擎实例 / DOM 根」。判据不是「谁先用到」，是「谁在语义上拥有它」。
 */
const { flowStores, graphEls, bindGraphEl, setFlowStore, updateAllNodeInternals } =
  useGraphRegistries()

const { onMiniMapPointerDown, onMiniMapPointerMove, onMiniMapPointerUp } =
  useMiniMapGesture({
    focusedId,
    focusAreaById,
    graphEls,
    flowStores,
    touchDevice: () => touchDeviceRef.value,
  })

/**
 * 画布上的双指手势（平移 + 缩放）。
 *
 * ⚠️ 它自己挂在 `.np-graph` 的 **capture** 阶段 —— 见那个文件的头注释：
 * 两指以上时要把事件从引擎那里拦下来，否则会同时开始框选。
 */
const canvasPinch = useCanvasPinch({
  flowStore: computed(() => flowStores.get(focusedId.value) ?? null),
  graphEls,
  focusedId,
})

/** 布局持久化用的 key（只存布局，不存模板内容） */
const LAYOUT_STORAGE_KEY = 'node-print.layout.v1'

/**
 * 切换某个 Area 的最大化。
 *
 * ⚠️ 是**切换**而不是「设为 true」：Area header 上只有一个按钮，
 * 再点一次应该还原。`maximize(null)` 表示还原。
 */
/**
 * 标签栏的三个动作。
 *
 * ⚠️ 「存为工作区」会弹一个 `prompt` —— 这是这个界面里**唯一**用原生
 * 输入框的地方。理由：它是低频、一次性、需要一段文字的动作，
 * 为它做一个自定义输入弹层（还要管焦点、Esc、移动端键盘）不划算。
 * 真正的重命名/管理入口将来放「窗口 → 工作区」里再做好。
 */
function onPickWorkspace(id: string) {
  const entry = workspaces.value.find((item) => item.id === id)
  if (entry) applyWorkspace(entry)
}

function onSaveWorkspace() {
  const label = prompt('工作区名称', `工作区 ${workspaces.value.length + 1}`)
  if (label === null) return
  saveWorkspace(label)
}

function onRemoveWorkspace(id: string) {
  const entry = workspaces.value.find((item) => item.id === id)
  if (!entry?.removable) return
  /* 删掉当前所在的工作区之后，布局本身不动 —— 只是它不再属于任何工作区 */
  if (!confirm(`删除工作区「${entry.label}」？当前窗口排布不会变。`)) return
  deleteWorkspace(id)
}

/**
 * 从分割把手拖出来的分割 —— 边界落在松手处。
 *
 * ⚠️ 复用 `splitArea` 的 `ratio` 参数（布局层一行新逻辑都不需要）：
 * 它本来就会 `clampRatio` + `assertLayout`，所以拖到区域外也不会
 * 产生非法的树。这也是这一步敢做的原因 —— 布局层的操作早就是纯函数了，
 * C⑦ 加的只是**指针交互**。
 */
function splitAt(areaId: string, dir: 'row' | 'col', ratio: number) {
  layout.value = splitArea(layout.value, areaId, dir, ratio)
}

/**
 * 拖分隔条**拖过头** → 把那一侧的 Area 并掉。
 *
 * ⚠️ 这里**再判一次**能不能并（而不是只信树传上来的 id）：
 *   · 树只有自己那棵子树，判不了「是不是最后一个 Area」；
 *   · `joinTarget` 是纯函数，重判一次的成本可以忽略，
 *     而漏判会让工作区被并空 —— 那时没有任何东西能承载编辑器。
 */
function joinAreaById(areaId: string) {
  if (countAreas(layout.value.root) <= 1) return
  if (!findArea(layout.value.root, areaId)) return
  layout.value = closeAreaOf(layout.value, areaId)
}

/**
 * 右键 Area 头 → 开这个 Area 自己的菜单。
 *
 * ⚠️ 同时把它设为**焦点**：菜单里的「最大化 / 关闭 / 交换」都是
 * 针对「我右键的那个」的，而焦点决定状态栏和一堆命令打给谁 ——
 * 不切的话会出现「菜单是对着 B 开的，但操作作用在 A 上」。
 */
function onAreaMenu(areaId: string, event: MouseEvent) {
  focusAreaById(areaId)
  menu.value = { kind: 'area', x: event.clientX, y: event.clientY, areaId }
}

/**
 * 点了窗口 header 上的**窗口类型图标**。
 *
 * ⚠️ 开的是**同一套 Area 菜单**（只是锚在按钮下方），不另造一种菜单 ——
 * 那份菜单的第一项就是「窗口类型」，内容完全一致。
 * 两种入口吃同一份数据，不会分叉（这个仓库为「同一份清单两份」记过好几条）。
 */
function onEditorMenu(areaId: string, anchor: HTMLElement | null) {
  /*
   * ⚠️ **再点同一个图标 = 关掉菜单。** 这个判断必须在宿主做 ——
   * `NpArea` 那边的局部开关状态已经被删掉（见那里的注释：
   * 它既不会重置，又会随组件实例复用串到别的 Area 上）。
   */
  if (menu.value?.kind === 'editor' && menu.value.areaId === areaId) {
    closeMenu()
    return
  }
  /*
   * ⚠️ **先开菜单，再切焦点**（原来反着）。
   * `focusAreaById` 会让布局树重渲染 —— 那一步放在前面时，
   * 新分屏出来的 Area 上点图标会**什么都开不出来**（实测：
   * 第一个 Area 的图标出 8 个菜单项，新 Area 的出 0 个，
   * 而同一个新 Area 的「⋯」是好的）。顺序换过来之后菜单先落地，
   * 焦点随后跟上，两者互不干扰。
   */
  const rect = anchor?.getBoundingClientRect()
  /*
   * ⚠️ `kind: 'editor'` —— **只列窗口类型**，不是整套 Area 菜单。
   * 点左上角图标的人要的是「换个窗口」，不该先看到一排结构操作。
   * 结构操作（分屏 / 最大化 / 关闭）走右边那个「⋯」（`kind: 'area'`）。
   */
  menu.value = {
    kind: 'editor',
    x: rect?.left ?? 0,
    y: (rect?.bottom ?? 0) + 4,
    areaId,
  }
  focusAreaById(areaId)
}

/**
 * 侧栏开合之后。
 *
 * ⚠️ 画布宽度变了，必须让 Vue Flow 重新量一次 ——
 * 不量的话节点和连线的位置会按**旧宽度**算一帧，表现为
 * 「开关侧栏时连线闪一下错位」。
 */
function onPanelToggled(): void {
  void nextTick(() => updateAllNodeInternals([...selectedIds.value]))
}

function onToggleMaximize(areaId: string) {
  maximize(maximizedId.value === areaId ? null : areaId)
}

/**
 * 菜单有五种，共用同一个组件：
 *
 *   context —— 画布右键：对当前选择的操作
 *   add     —— 添加节点（Shift+A / 双击画布 / 右键里的一项）
 *   asset   —— 添加资产（组节点资产：搭好的子图，当单个节点用）
 *   link    —— 拖线松手落空：列出能接上的东西（Blender 的 link drag search）
 *   bar     —— 顶栏菜单（文件 / 编辑 / 视图 / 窗口）
 *   area    —— 右键 Area 头：只对**那一个区域**成立的操作（交换 / 最大化 / 关闭）
 *
 * 前几个项少，直接列；`link` 项可能很多，所以要能搜。
 */
/*
 * ⚠️ `MenuKind` 从 `useMenus` **import**，不在这里再写一遍 ——
 * 这里原来是第二份定义（少了 `asset`），于是「宿主认为不可能的 kind」
 * 在模板里被 vue-tsc 报成「没有重叠」。两处枚举不同步是必然的，
 * 只是什么时候暴露而已。
 */

interface LinkMenuContext {
  /** 这次要接的起点端口（从输入拖出来时是**原输出端口**） */
  origin: HandleRef
  /** 松手的屏幕坐标 —— 新建的节点就落在这里 */
  point: { x: number; y: number }
  /** 从已有输入拖出来时带的原边：菜单被关掉（没选东西）就是「断开」 */
  undoEdge?: EdgeRef
}

const menu = ref<{
  kind: MenuKind
  x: number
  y: number
  link?: LinkMenuContext
  /** `kind === 'area'` 时：右键的是哪一个 Area（`areaSections` 按它生成菜单） */
  areaId?: string
} | null>(null)

/** 右键落在某条连线上时的边 id（选中/删除/静音都作用在它上面） */
const contextEdgeId = ref<string | null>(null)

/**
 * 区分「在菜单里选了某一项」和「直接把菜单关掉」（link 菜单里等于「断开」）。
 *
 * ⚠️ 必须和 `menu` / `contextEdgeId` 一起提前声明：`useGestureMachine`
 * 的 ctx 要用它（`runGestureCommands` 里会写 `menuPicked.value = false`）。
 * 声明落在那个调用之后，桥接对象的 getter 一执行就会
 * `Cannot access 'menuPicked' before initialization`。
 */
const menuPicked = ref(false)

/**
 * 鼠标在画布上的最后位置（屏幕坐标）。
 *
 * ⚠️ 它和上面的 `graphRef` 都**上移到了这一段之前**：`useAddNode` 要注入它们，
 * 而 composable 是**立即执行**的（声明留在原处会 TS2448 / 运行期读不到）。
 * 写入方仍是下面的 `onCanvasPointerMove`。
 *
 * `Shift+A` 想「就地弹出」，但**键盘事件本身不带坐标** ——
 * 所以只能靠指针移动时先记下来。
 *
 * 不是响应式的：每帧都在写，做成 ref 会白白触发一堆重算。
 */
let lastCanvasPoint: { x: number; y: number } | null = null

/* ── 状态与派生量（从 bundle 还原）─────────────────────────── */

/*
 * ⚠️ 这里原来有 `const t = reactive({ socketSize, rowHeight, … })`
 * 和一段 `tokenCss` —— 「实时调参」面板的数据侧。
 * **整块删除**（连同面板、`tokenCss` 的 `<style>` 注入、`resetTokens`）：
 * 那些旋钮值早就固化进 `theme.css` 的 `--np-*` 令牌了，
 * 留着注入逻辑只会让「看到的样式」和「文件里的样式」有两份来源。
 */

/*
 * ★ **运行**这条线（`useRuns`）—— 谁触发、跑哪条链、结果留给谁看。
 *
 * ⚠️ 手动触发的节点类型**从注册表查**（`trigger === 'manual'`），不写死
 * `'event.manual'`：内核里出现包的节点类型标识是被 `verify:boundary` 明令禁止的
 * （那是"内核认识领域"的入口），而且写成查表之后，**任何包**的
 * 手动触发器都能被这个按钮带动。
 */
const runs = useRuns({
  models: () => evalModels.value,
  edges: () => evalEdges.value,
  evaluators: () => BUILTIN_EVALUATORS,
  runners: () => BUILTIN_RUNNERS,
  nodeTypes: () => NODE_TYPES,
  /* 装卸包会原地改那两张表 —— 读一下版本号，闸门才会跟着重算 */
  version: () => packagesVersion.value,
})

/**
 * 运行按钮的 tooltip —— 四种状态各自说清。
 *
 * ⚠️ 能力不足时**在这就说清缺什么**（而不是点了才报错）：
 * 按钮灰着 + 悬停有原因，比"点了没反应"少一次困惑。
 */
const runTitle = computed(() => {
  const phase = runs.phase.value
  if (phase === 'running') return '运行中…（点击取消）'
  if (phase === 'queued') return `排队中（前面还有 ${runs.queued.value} 次）· 再点一次可取消`
  if (!runs.gate.value.ok) return `跑不了：${runs.gate.value.reason}`
  const ids = manualTriggerIds.value
  const last = runs.last.value
  const tail = last ? ` · 上次：${summarizeRun(last)}` : ''
  return ids.length > 0
    ? `运行：跑这 ${ids.length} 个「手动触发」节点的下游${tail}`
    : `运行整张图${tail}`
})

/** 图里所有"手动触发"类的节点（按注册表筛，不认具体包） */
const manualTriggerIds = computed(() =>
  evalModels.value.filter((model) => nodeTypeOf(model.type)?.trigger === 'manual').map((m) => m.id),
)

/**
 * 点「运行」。
 *
 * ⚠️ 有手动触发节点时**只跑它们的下游闭包**（那是"事件驱动"的语义：
 * 谁触发就跑谁那条链）；一个都没有时跑整张图 —— 那是"我只是想看看结果"。
 * ⚠️ 正在跑时点它是**取消**（按钮上会写明），不是再排一次队 ——
 * 用户看到"运行中"想的大多是"停下"。
 */
async function runManual(): Promise<void> {
  if (runs.phase.value === 'running') {
    runs.cancel()
    return
  }
  if (!runs.gate.value.ok) {
    notice.value = `跑不了：${runs.gate.value.reason}`
    return
  }
  const ids = manualTriggerIds.value
  const record = await runs.run(ids.length > 0 ? ids : undefined)
  notice.value = summarizeRun(record)
}

const {
  levelPath,
  draggingNodes,
  evalModels,
  evalEdges,
  enterSubgraph,
  exitSubgraph,
  findSocket,
  socketTypeOf,
  linkInput,
  clearSelection,
  isProtectedNode,
  refreshInterfaceOf} = useSubgraphLevels({
  /*
   * ⚠️ 这两个要转一手：Vue Flow 的 `Node` / `Edge` 泛型太深，
   * 直接传会让 TS 的推断爆栈（TS2589）。Playground 里到处都这么干。
   */
  nodes: nodes as unknown as SubgraphCtx['nodes'],
  edges: edges as unknown as SubgraphCtx['edges'],
  flowStore,
  activeNodeId,
  currentModels,
  edgeViews,
  looseNodes,
  modelOf,
  makeFlowNode,
  /* `makeEdge` 现在要显式拿端口类型（纯函数）——包一层适配接线类型 */
  makeEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) =>
    makeEdge(
      (nodeId?: string | null, handleId?: string | null) => socketTypeOf(nodeId, handleId),
      source,
      sourceHandle,
      target,
      targetHandle,
    ),
  showNotice,
  unwrapArray
});
/* 初始没有连线 —— 和 `nodes` 一样，内核不预置内容 */
const touchMenu = useTouchMenu({
  /*
   * ⚠️ `as never`：`useTouchMenu` 的 `PointerLikeEvent` 和
   * `useMenus` 收的 `MouseEvent | TouchEvent` 是**结构不同名**的两个类型。
   * 本文件里跨 composable 桥接事件时都用这个写法（见 `sendGesture`、
   * `runNoticeAction`），不去把两边的类型对齐 —— 那要改公共签名，
   * 而它们描述的是同一件事。
   */
  onNodeMenu: (p) => onNodeMenu(p as never),
  /*
   * ⚠️ 这里**不加 `as never`** —— 上一版加了，正好把「touch 层传
   * `{ event }`、实现收事件本身」这个不兼容形状抹平了，
   * 于是长按空白画布必崩而类型检查全绿。
   * 参数类型现在两边都收得下（结构类型），不需要断言。
   */
  onPaneMenu: (ev) => onPaneMenu(ev),
  onEdgeMenu: (p) => onEdgeMenu(p as never),
})
function onTouchLongPressStart(ev: PointerEvent) {
  touchMenu.onPointerDown(ev)
}
function onTouchLongPressMove(ev: PointerEvent) {
  touchMenu.onPointerMove(ev)
}
/*
 * ⚠️ **两个 handler 要合并成一个**：同一个元素上写两条 `@pointerup`
 * 是**重复属性**，后一条会覆盖前一条 —— 表现是"某个手势悄悄失效"，
 * 而模板编译不报错。踩过一次，所以这里是显式合成的。
 */
/*
 * ⚠️ 两个 **capture** 阶段的按下处理合成一个入口：Alt+左键的划痕，
 * 以及"第二根手指落下就接管"的双指手势。
 * 分开写成两条 `@pointerdown.capture` 是**重复属性**，后一条会覆盖前一条。
 */
function onCanvasPointerDownCapture(ev: PointerEvent) {
  onLassoDown(ev)
  canvasPinch.onPointerDown(ev)
  /*
   * ⚠️⚠️ **双指接管时必须显式把长按取消掉。**
   *
   * `canvasPinch` 在第二根手指落下时会 `stopPropagation()` ——
   * 那是为了不让引擎开始框选，但它**顺带也挡住了同一个元素上的
   * `@pointerdown="onTouchLongPressStart"`**。
   * 于是 `useTouchMenu` 永远不知道有第二根手指，它那边的 `pointers`
   * 始终是 1，**第一根手指起的定时器照跑** → 超时弹菜单。
   *
   * 实测反馈就是这个：「双指移动时间超过会弹出菜单,双指缩放超时也会弹出菜单」。
   * （单测里我直接调 `useTouchMenu.onPointerDown` 造两根手指，所以那条路径
   * 是绿的 —— **测试绕过了真实的 DOM 传播**，这个差别就是它漏掉的原因。）
   */
  if (canvasPinch.isPinching()) touchMenu.onPointerCancelAll()
}

function onCanvasPointerUp(ev: PointerEvent) {
  canvasPinch.onPointerUp(ev)
  touchMenu.onPointerUp(ev)
}
/*
 * ⚠️ `pointercancel` 也要**清空所有手指**：系统手势（下拉通知栏、
 * 切应用）会取消指针序列，而那时 `pointerup` **不会**来 ——
 * 不清的话 `pointers` 会一直留着一个 id，之后就再也起不了长按。
 */
function onCanvasPointerCancel(ev: PointerEvent) {
  canvasPinch.onPointerCancel()
  touchMenu.onPointerUp(ev)
  touchMenu.onPointerCancelAll()
}
/**
 * 「某节点的某个端口是什么类型」—— 传进来而不是直接读闭包，
 * 这样 `makeEdge` / `seedEdges` 是**纯函数**（好测，也没有初始化顺序问题）。
 */
type SocketOf = (nodeId?: string | null, handleId?: string | null) => SocketType | undefined

function makeEdge(
  socketOf: SocketOf,
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
) {
  const type = socketOf(source, sourceHandle) ?? "any";
  return {
    /* ⚠️ 边 id 的命名约定只从 `edgeOps` 来（原来五处各写一遍） */
    id: edgeIdOf(source, sourceHandle, target, targetHandle),
    source,
    target,
    sourceHandle,
    targetHandle,
    /*
     * ⚠️ 走**主题读回**而不是静态色表。
     * 连线是 Vue Flow 的 style 对象，`var()` 够不到 —— 只能由 JS 取值。
     * 但取的是「主题解析之后的当前值」，所以主题换了它跟着变。
     */
    style: { stroke: themeSocketColor(type) },
    data: { socketType: type }
  };
}
const { commit, doUndo, doRedo, clearHistory, historyState } = useUndoRedo({
  /*
   * ⚠️ 这两个要转一手：Vue Flow 的 `Node` / `Edge` 泛型太深，
   * 直接传会让 TS 的推断爆栈（TS2589）。
   */
  /*
   * ⚠️ 这两个要转一手：Vue Flow 的 `Node` / `Edge` 泛型太深，
   * 直接传会让 TS 的推断爆栈（TS2589）。
   */
  nodes: nodes as unknown as UndoRedoCtx['nodes'],
  edges: edges as unknown as UndoRedoCtx['edges'],
  flowStore,
  showNotice,
})
const { clipboardState, doCopy, doPaste } = useClipboard({
  nodes: nodes as unknown as ClipboardCtx['nodes'],
  edges: edges as unknown as ClipboardCtx['edges'],
  flowStore,
  /*
   * ⚠️ 包一层再传：`selectedIds` 声明在这段之后，直接传 ref 会在
   * composable 初始化时读到 `undefined`。传闭包 = 调用时才读。
   */
  selectedIds: () => selectedIds.value,
  commit,
  showNotice
});
const {
  gesture,
  linkingNodeId,
  linkingHandleId,
  linkingType,
  linkingSide,
  linking,
  lassoMode,
  lassoPath,
  isStructurallyValid,
  alreadyConnected,
  isValidConnection,
  inferListItemType
} = useGestureState({
  socketTypeOf,
  findSocket,
  linkInput,
  showNotice});
function onConnectStart(params: {
  /* ⚠️ 按 Vue Flow 的 `OnConnectStartParams` 写：`nodeId` 是**可选**的，
     `event` 只有 MouseEvent。写宽了反而不接受（形参逆变）。 */
  event?: MouseEvent
  nodeId?: string
  handleId: string | null
  handleType?: 'source' | 'target'
}) {
  const ev = params.event
  sendGesture({
    type: "connectStart",
    nodeId: params.nodeId ?? '',
    handleId: params.handleId ?? null,
    handleType: params.handleType ?? 'source',
    /* `clientX` 只在 MouseEvent 上有；触摸是 changedTouches —— 统一走 eventPoint */
    point: ev ? eventPoint(ev as MouseEvent) : { x: 0, y: 0 },
  });
}
function onConnect(conn: {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}) {
  sendGesture({
    type: "connect",
    source: conn.source,
    sourceHandle: conn.sourceHandle ?? null,
    target: conn.target,
    targetHandle: conn.targetHandle ?? null
  });
}
function onConnectEnd() {
  sendGesture({ type: "connectEnd" });
}
/*
 * 关掉 Area 时把它的登记清掉。
 *
 * ⚠️ 不清理的话 Map 会随着「开窗口 / 关窗口」一直涨，
 * 而且预设**会复用 Area id**（`p-console` 等是写死的），
 * 旧 store 留在表里就可能被下一次读到。
 */
watch(areaIds, (ids) => {
  const alive = new Set(ids)
  let changed = false
  for (const id of [...flowStores.keys()]) {
    if (!alive.has(id)) {
      flowStores.delete(id)
      changed = true
    }
  }
  for (const id of [...graphEls.keys()]) {
    if (!alive.has(id)) {
      graphEls.delete(id)
      changed = true
    }
  }
  if (changed) storesTick.value += 1

  /*
   * 视口记忆也要跟着剪。
   * ⚠️ 不剪的话这份记录只增不减：预设反复切换、Area 反复开关，
   * 存储里会攒下一堆再也用不到的 id（而老 id 可能被复用 —— 那时
   * 新 Area 会得到一个**不属于它**的视口）。
   */
  /*
   * 滚动记忆也要剪（它只在内存里，但 Area 反复开关同样会让它只增不减）。
   */
  const aliveScrolls = Object.fromEntries(
    Object.entries(areaScrolls.value).filter(([id]) => alive.has(id)),
  )
  if (Object.keys(aliveScrolls).length !== Object.keys(areaScrolls.value).length) {
    areaScrolls.value = aliveScrolls
  }

  const aliveViewports = pruneViewportMemory(areaViewports.value, ids)
  if (Object.keys(aliveViewports).length !== Object.keys(areaViewports.value).length) {
    areaViewports.value = aliveViewports
    persistViewportMemory(viewportStorage, aliveViewports)
  }
})

/** 当前**焦点** Area 的画布元素（同上：命令类查询都以焦点为准） */
const graphRef = computed<HTMLElement | null>(() => {
  void storesTick.value
  return graphEls.get(focusedId.value) ?? null
})

/**
 * 模板上的函数式 ref：**每个 Area 各注册自己那一份**。
 *
 * ⚠️ 不能再用 `ref="graphRef"` —— 同一个模板 ref 名在多个 Area 上
 * 会被反复赋值，最后渲染的那个赢（就是上面那个 bug）。
 * 传 `null` 表示卸载，要把那一项删掉，否则 Map 会一直涨。
 */
function queryGraph<T extends Element = Element>(selector: string): T | null {
  return (graphRef.value?.querySelector(selector) as T | null) ?? null
}

/** 同上，复数版本 */
function queryAllGraph<T extends Element = Element>(selector: string): T[] {
  const host = graphRef.value
  if (!host) return []
  return Array.from(host.querySelectorAll(selector)) as T[]
}

/**
 * 通知**所有**节点图视图重算端口位置。
 *
 * ⚠️ 只通知焦点视图是不够的：另一个窗口会停在旧几何上
 * （边的端点对不上节点、handle 位置偏移）。用户的原话是
 * 「blender 是同步刷新的」—— 多个视图看的是同一张图，
 * 数据一变就该**都**重算，只是各自的视口独立。
 */

/**
 * 「工具设置」—— 曾经是**实时调参面板**的那 5 个旋钮。
 *
 * ⚠️ 面板已经删除（用户实测：「去掉右侧浮窗和调参窗口，没啥用」），
 * 这几个值现在只剩**模板文件往返**一个用途：文件格式里有 `tool` 字段
 * （见 `useTemplateFile`），留着它是为了老文件读得进来、新文件写得出去。
 *
 * 它们**不再影响渲染** —— 真正的样式真值是 `theme.css` 的 `--np-*` 令牌。
 * 哪天确认没有老文件需要兼容，就把这段和文件格式里的 `tool` 一起删掉。
 */
const toolSettings = reactive({
  socketSize: 11,
  rowHeight: 22,
  radius: 8,
  headerHeight: 26,
  labelRatio: 44,
})
/*
 * ── 最近用过的节点 ──────────────────────────────────────────
 *
 * ⚠️ 必须在 `useAddNode` **之前**建：菜单顶部的「最近」那一组是它算出来的。
 */
const { recentTypes, remember: rememberNodeType } = useRecentNodes()

const {
  addSections,
  assetSections,
  addAssetAt,
  toFlowPoint,
  viewportCenter,
  focusNode,
  addNodeAt,
  openAddMenu,
  openAssetMenu,
} = useAddNode({
  recentTypes,
  rememberNodeType,
  nodes: nodes as unknown as AddNodeCtx['nodes'],
  flowStore,
  graphRef,
  activeNodeId,
  /*
   * ⚠️ `menu` 声明在「右键菜单」段（本段之后），所以用闭包；
   * 而 `openAddMenu` 真正被调用时 `menu` 早就初始化完了。
   */
  menuPoint: () => menu.value ? { x: menu.value.x, y: menu.value.y } : null,
  /*
   * ⚠️ 带 `kind` —— 「添加节点」是 `add`，「添加资产」是 `asset`。
   * 第一版这里写死成 `kind: 'add'`，于是点「添加资产」开出来的还是
   * **节点目录**（表现：资产菜单里全是节点类型，看着像功能没做）。
   */
  openMenuAt: (point, kind) => {
    menu.value = { kind, x: point.x, y: point.y };
  },
  lastCanvasPoint: {
    get value() {
      return lastCanvasPoint;
    },
    set value(next) {
      lastCanvasPoint = next;
    }
  },
  nextNodeId,
  makeFlowNode,
  commit,
  showNotice
});
function onCanvasPointerLeave() {
  lastCanvasPoint = null;
  socketTip.value = null;
}
const { exportRenderTree } = useExportIr({
  nodeViews,
  edgeViews,
  isEditorOnlyType: (type) => Boolean(nodeTypeOf(type)?.editorOnly),
  /* 「这个类型来自哪个包」—— 写进文档的 `pkg`（工具侧查完备性要用） */
  packageOf: nodePackageOf,
  showNotice
});
/*
 * ── 节点包（装卸 + 落盘）────────────────────────────────────
 *
 * ⚠️ 它必须在**所有**读节点目录的东西**之前**建好：装卸会重建注册表，
 * 而重建是原地更新（见 `nodeTypes.ts` 的注释），
 * 所以早读晚读都对 —— 但版本号要能被下游依赖到。
 */
const {
  installed: installedPackages,
  problems: packageProblems,
  install: installPackage,
  importFromFile: importPackage,
  uninstall: uninstallPackage,
  enable: enablePackage,
  exportPackage,
} = usePackages({ showNotice })

/*
 * ── 包贡献的编辑器：把契约里的 `unknown` 收窄成 Vue 组件 ──────
 *
 * ⚠️ 这两行 `as Component` 就是**宿主那一侧的全部适配**：
 * `types/editor.ts` 里 `component` 是 `unknown`（那一层不认识 Vue，
 * 见 `package-boundaries.md` 的 L1），而这里知道渲染它的是 Vue。
 * 内核的编辑器没有 `component`（它们由下面的 `v-if` 链分派），
 * 所以这两个函数对内核那几条一律返回 null。
 *
 * ⚠️ 传进来的必须是 `editorOf(area)`（**显示的那个**）：
 * `area.editor` 可能指向一个没装的包（那是"意图"），
 * 查它只会得到 undefined —— 窗口就变成一片空白。
 */
function packageEditorOf(editor: EditorType): Component | null {
  return (EDITOR_META[editor]?.component as Component | undefined) ?? null
}

/**
 * 包贡献的**窗口头部**控件（`EditorMeta.header`）。
 *
 * ⚠️ 宿主只做"查表 + 摆位置"，不认识组件内容 —— 这是 `header` 这条能力
 * 存在的全部意义（否则宿主就得为每个包写一段 `v-if`，那是 B 路）。
 */
function headerComponentOf(editor: EditorType): Component | null {
  return (EDITOR_META[editor]?.header as Component | undefined) ?? null
}

/**
 * 某个窗口类型在底部提示条上显示的操作提示。
 *
 * ⚠️ 只有**包声明了**才有（`EditorMeta.tips`）—— 内核不猜、
 * 也不替别的窗口编一套"通用快捷键"。没有就是空的（提示条只剩版本号）。
 */
function tipsOf(editor: string): readonly EditorTip[] {
  return EDITOR_META[editor]?.tips ?? []
}

/**
 * 提示条上**当前该显示**的那几条。
 *
 * ⚠️ 桌面端与触摸端说的不是一回事：手机上没有滚轮 / Delete / Ctrl+Z，
 * 有的是手指操作。所以按 `touchDevice` 二选一（没有对应说法的直接不显示 ——
 * 在手机上写"滚轮缩放"比不写更糟）。
 */
function visibleTips(editor: string): readonly { keys: string; label: string }[] {
  /* 判定在 `layout/editorTips.ts`（纯函数，可断言 —— 无头环境翻不动 touchDevice） */
  return pickTips(tipsOf(editor), touchDevice.value)
}

/** 包贡献的侧栏面板：tab 自己带着组件（内核的 tab 没有，走宿主分派） */
function packagePanelOf(editor: EditorType, side: PanelSide, tab: string): Component | null {
  return (panelTabOf(editor, side, tab)?.component as Component | undefined) ?? null
}

/*
 * ⚠️ 这里原来有一个 `watch(packagesVersion, settleEditors)` ——
 * 卸载贡献编辑器的包时把那些窗口**改写成**节点图。
 * **删掉了**（2027）：窗口现在记的是**意图**（`AreaLeaf.editor`），
 * 显示的是 `editorOf(area)` 解析出来的**现状** —— 包一卸载，
 * 解析自然回落到默认编辑器；装回来它自己就回来了。
 * 于是宿主不再需要对这个事件做任何补救动作（少一个 watch、少一次树改写）。
 */

const {
  saveTemplateFile,
  applyFile,
  currentFile,
  downloadFile,
  downloadDocument
} = useTemplateFile({
  nodes: nodes as unknown as TemplateFileCtx['nodes'],
  edges: edges as unknown as TemplateFileCtx['edges'],
  tool: toolSettings,
  nodeViews,
  edgeViews,
  /* 同 `useExportIr`：文档里逐节点记来源包 */
  packageOf: nodePackageOf,
  makeFlowNode,
  afterApply: () => {
    clearSelection();
    /*
     * ★★ **换了整张图 → 历史必须清空**。
     *
     * 历史里存的是**旧那份文档**的快照。不清的话，打开另一份文档之后按
     * Ctrl+Z 会把旧文档的图装进新文档里（表现是"我别的文档的内容跑进来了"），
     * 再存一次就落盘成事实。细节见 `useUndoRedo.clearHistory` 的注释。
     */
    clearHistory();
    nextTick(() => updateAllNodeInternals());
  },
  showNotice
});
const { connectableFor, repointConnectionLine } = usePointerTracking({
  gesture,
  flowStore,
  queryGraph,
  /*
   * ⚠️ `linking` 是这个接口要求的字段，但当前的调用漏了它
   * （接口后来加了它）。它来自上面 useSubgraphLevels 的解构 ——
   * 同一个值 useProjection 那边也传了。
   */
  linking,
  linkingNodeId,
  linkingHandleId,
  linkingSide,
  currentModels,
  edgeViews,
  /* ⚠️ sendGesture 声明得比这段晚 —— 只在事件回调里调用，闭包注入 */
  sendGesture: (event) => sendGesture(event as never),
})
function nodeBoxOf(id: string): AlignBox | null {
  /*
   * ⚠️ `NodeView` 是**窄接口**（只有 id / data / selected），
   * 而 `nodeViews()` 实际返回的是 Vue Flow 的节点对象（带 `position`）。
   * 这里显式补上缺的那一小块，而不是去放宽公共接口 ——
   * 别的消费者不需要 position。
   */
  const view = nodeViews().find((n) => n.id === id) as
    | (NodeView & { position?: { x: number; y: number } })
    | undefined
  if (!view) return null
  const position = view.position
  const el = queryGraph<HTMLElement>(`.${FLOW_DOM.node}[data-id="${id}"]`)
  return {
    id,
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: modelOf(id)?.width ?? MIN_NODE_WIDTH,
    height: el?.offsetHeight ?? 80
  };
}
function handleScreenCenter(nodeId: string, handleId: string) {
  const host = graphRef.value
  const el = queryGraph(
    `.${FLOW_DOM.handle}[${HANDLE_NODE_ATTR}="${nodeId}"][${HANDLE_ID_ATTR}="${handleId}"]`,
  )
  if (!host || !el) return null;
  const hostRect = host.getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2 - hostRect.left,
    y: rect.top + rect.height / 2 - hostRect.top
  };
}
const linkPreview = computed(() => {
  const current = menu.value;
  if (!current || current.kind !== "link" || !current.link) return null;
  const host = graphRef.value;
  if (!host) return null;
  const origin = current.link.origin;
  const from = handleScreenCenter(origin.nodeId, origin.handleId);
  if (!from) return null;
  const hostRect = host.getBoundingClientRect();
  return { from, to: { x: current.x - hostRect.left, y: current.y - hostRect.top } };
});
const linkPreviewPath = computed(() => {
  const p = linkPreview.value;
  if (!p) return "";
  const dx = Math.max(24, Math.abs(p.to.x - p.from.x) * 0.5);
  return `M ${p.from.x} ${p.from.y} C ${p.from.x + dx} ${p.from.y}, ${p.to.x - dx} ${p.to.y}, ${p.to.x} ${p.to.y}`;
});
const levelSignature = () => levelPath.value.map((s) => s.nodeId).join("/");
levelSignature();
watch(
  () => edgeViews().map((e) => e.id ?? `${e.target}.${e.targetHandle}`).join("|"),
  (next, prev) => {
    const before = (prev ?? "").split("|").filter(Boolean);
    const after = (next ?? "").split("|").filter(Boolean);
    before.filter((id) => !after.includes(id));
    return;
  }
);
useMutedEdgeStyle({
  edges: edges as unknown as MutedEdgeStyleCtx['edges'],
  nodeViews,
  /* 当前接口要求端口类型（用来算静音后的颜色） */
  socketTypeOf: (nodeId?: string | null, handleId?: string | null) =>
    socketTypeOf(nodeId, handleId),
})
/*
 * 小地图的节点配色。
 *
 * ⚠️ 这两个函数由 Vue Flow 的 MiniMap 在**它自己的渲染里**调用，
 * 所以里面读 `theme/runtime` 的快照是**被追踪的** —— 主题一变，
 * 快照的 `shallowRef` 一换，小地图就会重画。
 *
 * ⚠️ 兜底值（`#5a5a5a` / 黑色描边）不能删：第一帧读回还没发生，
 * 那时快照是空的。少了兜底会得到 `stroke=""` —— 描边**直接不可见**。
 */
function minimapNodeColor(node: { data?: NpNodeData }): string {
  return themeCategoryColor(node.data?.model?.category, '#5a5a5a')
}
function minimapNodeStroke(node: { data?: NpNodeData }): string {
  return node.data?.model?.muted
    ? themeVar('--np-minimap-muted', '#e05252')
    : themeVar('--np-minimap-node-stroke', 'rgba(0, 0, 0, 0.5)')
}
const {
  sampleEdgePathFine,
  collectEdgeCandidates,
  toFlowPoints,
  onLassoDown,
  lassoPoints
} = useCutLasso({
  flowStore,
  graphRef,
  queryAllGraph,
  lassoPath,
  /* ⚠️ sendGesture 声明得比这段晚 —— 闭包注入 */
  sendGesture: (event) => sendGesture(event as never),
})
/*
 * ⚠️ 这个不变量检查原来**没有 DEV 门禁**，而且挂在 `watchEffect` 上 ——
 * 每次连线变化都会重跑，一旦有重复就反复刷屏。
 * 它是给开发者的现场证据，不是给用户的提示：加 DEV 门禁，
 * 生产构建里连这个 effect 都不跑。
 */
if (import.meta.env.DEV) {
  watchEffect(() => {
    const duplicates = findDuplicateInputs(edgeViews());
    if (duplicates.length > 0) {
      console.warn("[node-print] 同一输入端口上出现多条连线：", duplicates);
    }
  });
}
useProjection({
  // Vue Flow 的 Edge 泛型太深，同上转一手
  nodes: nodes as unknown as ProjectionCtx['nodes'],
  edges: edges as unknown as ProjectionCtx['edges'],
  nodeViews,
  edgeViews,
  modelOf,
  socketTypeOf,
  linking,
  draggingNodes
});
const { applyConnection, putEdgeBack, sendGesture } = useGestureMachine({
  gesture: gesture as unknown as GestureMachineCtx['gesture'],
  edges: edges as unknown as GestureMachineCtx['edges'],
  flowStore,
  /* ⚠️ graphRef 声明在这段之后 —— 用闭包注入，避免 TS2448 */
  graphRef: () => graphRef.value,
  /*
   * `menu` / `contextEdgeId` / `menuPicked` 现在都声明在本段**之前**
   * （见上面「菜单状态（提前声明…）」），所以**直接传 ref**，不要再包桥接。
   *
   * ★ 这里踩过坑：原先给 `menu` 只写了 getter 没写 setter，
   *   `runGestureCommands` 的 `offerLinkCandidates` 分支会
   *   `menu.value = {...}` —— 于是抛
   *   `Cannot set property value of #<Object> which has only a getter`。
   *   表现是「拖线松手弹候选菜单」这条路径直接炸。
   *   教训：**桥接对象只要有一侧会被写，就必须 get + set 成对写。**
   */
  menu,
  edgeViews,
  /* ⚠️ 这两个声明在下面「剪线划痕」段 —— 闭包转发 */
  collectEdgeCandidates: () => collectEdgeCandidates(),
  toFlowPoints: (points) => toFlowPoints(points),
  socketTypeOf,
  isStructurallyValid,
  alreadyConnected,
  inferListItemType,
  /* `makeEdge` 现在要显式拿端口类型（纯函数）——包一层适配接线类型 */
  makeEdge: (source, sourceHandle, target, targetHandle) => makeEdge(
    (nodeId, handleId) => socketTypeOf(nodeId, handleId),
    source,
    sourceHandle,
    target,
    targetHandle
  ),
  /* ⚠️ repointConnectionLine 声明得比这段晚 —— 闭包转发 */
  repointConnectionLine: (nodeId, handleId) => repointConnectionLine(nodeId, handleId),
  menuPicked,
  commit,
  showNotice
});
const { linkCandidateSections } = useConnect({
  applyConnection,
  addNodeAt,
  findSocket,
  currentModels,
  isStructurallyValid,
  commit
});
/**
 * 某个 Area 的 VueFlow 实例就绪。
 *
 * ⚠️ 形参用 `unknown` 收：Vue Flow 的 `VueFlowStore` 与项目的
 * `FlowStoreApi` **不是**同一个类型（`startConnection` 的形参形状不同），
 * 写死 `FlowStoreApi` 反而不可赋值。收下来再转一次 ——
 * 适配层本来就是干这个的（见 graph/flowStore.ts）。
 *
 * ⚠️ **必须按 Area 存**，不能只留最后一个（那正是「新窗口抢走小地图」
 * 的根因，见 `flowStores` 的注释）。
 */
function onFlowInit(areaId: string, instance: unknown) {
  const store = instance as FlowStoreApi
  flowStores.set(areaId, store)
  storesTick.value += 1
  /*
   * ⚠️ 这里原来会打一次「选中态来源形状」（`getSelectedNodes` / `getNodes`
   * 是怎么实现的）。那是排查"选中态取不到"时的现场记录，问题早就定死了 ——
   * 留着只会在每次开窗口时往控制台丢一段没人看的信息。
   */
  /*
   * ★ 恢复这个 Area 上次的视口。
   *
   * 放在 `nextTick` 里：Vue Flow 初始化时会按 `defaultViewport` 摆一次画布，
   * 我们要**在后面**覆盖它，否则会被它盖回去。
   * 没有记忆时什么都不做 —— 让 `defaultViewport` 正常生效。
   */
  const remembered = areaViewports.value[areaId]
  nextTick(() => {
    if (remembered?.zoom) store.setViewport?.({ ...remembered })
    store.updateNodeInternals?.()
  })
}
function dataModel(data: NpNodeData) {
  return data.model
}
function setCollapsed(nodeId: string, value: boolean) {
  const node = nodeViews().find((n) => n.id === nodeId);
  if (node?.data) node.data.collapsed = value;
  nextTick(() => updateAllNodeInternals([nodeId]));
}
function onGroupToggle(nodeId: string, groupId: string, collapsed: boolean) {
  const group = modelOf(nodeId)?.rows.find((r) => r.id === groupId);
  if (group?.kind === "group") group.collapsed = collapsed;
  nextTick(() => updateAllNodeInternals([nodeId]));
}
function onWidgetUpdate(nodeId: string, rowId: string, widgetId: string, value: unknown) {
  const row = modelOf(nodeId)?.rows.find((r) => r.id === rowId);
  if (row && (row.kind === "property" || row.kind === "input") && row.widget) {
    row.widget.value = value;
  }
}
/**
 * **写一个节点的参数值**（特殊设置那条路：属性面板里的端口表 / 代码）。
 *
 * ⚠️ 它和 `onWidgetUpdate` 是**同一个写口**（改行的控件值 + 让节点重排），
 * 只是不需要 widgetId —— 面板按 rowId 写。两处共用一份实现，
 * 免得"面板改了值但节点没跟着重排"这种只在一边出现。
 */
/**
 * JS 节点的类型标识。
 *
 * ⚠️ 内核里**可以有**内核自己的节点类型名（`verify:boundary` 禁的是
 * "内核出现**包**的节点类型"）—— 但为了少一处硬编码，这里仍然从
 * `JS_NODE_TYPE.type` 读。
 */
const JS_NODE_TYPE_ID = JS_NODE_TYPE.type;

/**
 * **按端口声明 + 代码重建 JS 节点的行**（动态行）。
 *
 * 步骤（顺序有意义）：
 *   ① 由 `ports` / `code` 两个参数生成新的端口行（输出端口从 `return` 推）；
 *   ② 换掉模型的行；
 *   ③ 删掉那些"现在不存在的端口"上的线。
 *
 * ⚠️ 生成那一步是**内核里的纯函数**（`rebuildJsRows`）：它和"打开文档时
 * 归一化旧数据"用的是同一份实现 —— 两条路各写一遍必然会漂。
 */
function rebuildJsNodeRows(model: GraphNodeModel) {
  const rows = rebuildJsRows(model);
  /* 端口集合与悬空边的判定都在内核里（纯函数，有单测覆盖） */
  const handles = handlesOfRows(rows);
  model.rows = rows;
  const danglingIds = danglingEdgesAfter(
    edges.value as unknown as EdgeLike[],
    model.id,
    handles,
  );
  if (danglingIds.length > 0) {
    /*
     * ⚠️ 走 `onEdgesChange`（和画布上删线同一条路）——那里会更新 `edges.value`、
     * 落历史、并让下游重算。直接改数组的话画布上那条线还在（悬空的线）。
     */
    onEdgesChange(danglingIds.map((id) => ({ id, type: "remove" as const })));
  }
}

/**
 * **定位到一个节点**（运行记录里点节点名走这条）。
 *
 * ⚠️ 这里必须**归一化 id**：运行记录里的 id 是**带层级路径**的
 * （子图里求值时是 `group_x/const_y` 这种）—— 而宿主的 `nodes` /
 * `activeNodeId` 用的是**当前层**的纯 id。不归一化的话：
 * 活动节点设成一个不存在的 id → 高亮没有、属性面板空着、
 * 镜头也不会动。表现就是"点了没反应"（查了半天才定位到这一层）。
 *
 * ⚠️ 取值是路径的**最后一段**：那是这个节点在本层的 id
 * （层级路径只是为了在整份文档里唯一）。
 */
async function onLocateNode(nodeId: string): Promise<void> {
  /*
   * ⚠️ 运行记录里的 id 可能是**带层级路径**的（子图里求值时是
   * `group_x/const_y` 这种）—— 那说明这个节点**不在当前这一层**。
   * 直接拿它去 `focusNode` 的后果：活动节点设成一个不存在的 id，
   * 于是"高亮没有、属性面板空着、镜头不动"，看起来就是**点了没反应**
   * （这条我查了两轮：先怀疑选中态同步，其实是**层级**问题）。
   *
   * 正确做法：**沿着路径逐层进去**，到了那一层再定位。
   */
  const { levels, nodeId: plain } = splitLevelId(nodeId);
  if (!plain) return;
  for (const levelId of levels) {
    const current = levelPath.value[levelPath.value.length - 1]?.nodeId ?? null;
    if (current === levelId) continue;
    enterSubgraph(levelId);
    /* ⚠️ 每次进入都要等一拍：层级切换是异步的（换 nodes/edges 那一份） */
    await nextTick();
    if ((levelPath.value[levelPath.value.length - 1]?.nodeId ?? null) !== levelId) {
      notice.value = `定位失败：进不去 ${levelId} 这一层`;
      return;
    }
  }
  if (!modelOf(plain)) {
    /* 找不到就**出声**：静默失败是这类"点了没反应"最贵的形态 */
    notice.value = `定位失败：当前层找不到节点 ${plain}`;
    return;
  }
  focusNode(plain);
}

/**
 * **节点参数被改过的版本号**。
 *
 * ⚠️ 为什么需要它：节点模型里的控件值是**普通对象的字段**，不是响应式的 ——
 * 谁是靠"读那个字段"来渲染的（属性面板就是），谁就不会自动重算。
 * 节点自己那边靠 `updateAllNodeInternals` 强制重渲染，而面板在另一个组件里，
 * 拿不到那条通道。所以这里显式记一个版本号，让面板读一下它。
 *
 * （同一个坑在打印包的面板那边也踩过 —— 那次是靠读求值结果解决的。）
 */
const nodeEditVersion = ref(0)

/**
 * 面板写参数时可以带的一个开关：**这次改动要不要重建行**。
 *
 * ⚠️ 为什么需要它（这是"改了代码但端口不长"那半边的原因）：
 * 面板里的代码框是**边打边存**的（`input` 事件），而端口是**从代码推**的 ——
 * 每敲一个字符就重建一次行的话，中途那些半截代码（`return {`）会把端口
 * 暂时推成"一个「结果」"，于是**挂在 out1 上的线会被当成悬空边删掉**。
 * 用户只是打字打到一半，线却没了。
 *
 * 所以拆成两拍：
 *   · `input`（每个字符）→ 只**存值**，不重建 —— 代码绝不丢；
 *   · `change`（失焦）/ 面板收起 → 再**重建行**，此时代码是完整的。
 */
export interface RowWriteOptions {
  rebuild?: boolean;
}

function setNodeRowValue(
  nodeId: string,
  rowId: string,
  value: unknown,
  options?: RowWriteOptions,
) {
  const model = modelOf(nodeId);
  const row = model?.rows.find((r) => r.id === rowId);
  if (!model || !row) return;
  if (row.kind === "property" || row.kind === "input") {
    if (!row.widget) return;
    commit("改参数", 500);
    row.widget.value = value;
    /*
     * ★★ **动态行**：端口声明 / 代码变了，节点的行要跟着重建。
     *
     * 为什么必须在这里做：行是**端口的来源**（`findSocketIn` 扫的是 rows），
     * 声明里加了端口而行没加 = 面板里配了个"看不见也连不上"的端口；
     * 代码里多 return 了一个键而输出行没长 = 用户看到的还是旧端口。
     *
     * ⚠️ 重建时要**保住**其余控件的值（代码 / 名字），并按新端口集合
     * **删掉已经不存在端口的线** —— 不然会留下悬空的边（画面上是
     * 一条接在空气上的线，而校验会说"找不到端口"）。
     */
    const rebuild =
      model.type === JS_NODE_TYPE_ID &&
      (rowId === "ports" || (rowId === "code" && options?.rebuild));
    if (rebuild) rebuildJsNodeRows(model);
    nodeEditVersion.value += 1;
    nextTick(() => updateAllNodeInternals([nodeId]));
    return;
  }
  if (row.kind === "repeater") {
    /* 变参行的"几路"是视图状态（由边同步而来），面板不直接改它 */
    return;
  }
}
function onResize(nodeId: string, width: number) {
  commit("改宽", 500);
  const selected = selectedIds.value;
  const targets = selected.length > 1 && selected.includes(nodeId) ? selected : [nodeId];
  for (const id of targets) {
    const model = modelOf(id);
    if (model) model.width = Math.max(MIN_NODE_WIDTH, width);
  }
  nextTick(() => updateAllNodeInternals(targets));
}
function onRepeaterRemove(nodeId: string, rowId: string, index: number) {
  const row = modelOf(nodeId)?.rows.find((r) => r.id === rowId);
  if (!row || row.kind !== "repeater") return;
  /*
   * ⚠️ `next` 显式写成宽类型：元素是 `edges.value` 的元素和
   * `{...edge, targetHandle}` 的并集，直接 `[]` 让 TS 去推会爆栈（TS2589）。
   */
  const raw = edges.value
  const next: Record<string, unknown>[] = []
  for (const edge of raw) {
    if (edge.target !== nodeId) {
      next.push(edge);
      continue;
    }
    const at = repeaterIndexOf(row, String(edge.targetHandle ?? ""));
    if (at < 0) {
      next.push(edge);
      continue;
    }
    if (at === index) continue;
    next.push(
      at > index ? { ...edge, targetHandle: repeaterHandle(row, at - 1) } : edge
    );
  }
  edges.value = next as unknown as Edge[]
  row.connected = (row.connected ?? []).filter((_, i) => i !== index)
  nextTick(() => updateAllNodeInternals([nodeId]));
}
/** ★★ **节点头部动作**：类型声明 id，宿主在这里提供实现（见 `nodeActions.ts`） */
const nodeActions = createNodeActions({
  activate: (nodeId) => {
    activeNodeId.value = nodeId
  },
  revealProperties: () => revealEditorWindow('properties'),
})

/** 头部动作被点了（节点 id + 类型声明的动作 id） */
function onNodeAction(nodeId: string, actionId: string): void {
  const action = nodeActions[actionId]
  if (!action) {
    showNotice(`这个动作（${actionId}）没有实现 —— 是哪个包贡献的节点类型？`)
    return
  }
  action.run(nodeId)
}

/**
 * **下一个叠放号**（`max(现有 z) + 1`）——
 * "谁最后被选中/被创建，谁在最上面"就靠它单调递增。
 *
 * ⚠️ 只在超过上限时**重排一次**（把现有 z 按大小压成 0..n-1）：
 * 号会随着"点一下选一次"一直涨，不收敛的话总有一天撞上
 * `NpCanvasNode` 里那个 `Z_TIER`（分层用的大格子）。
 */
const Z_LIMIT = 50_000
function nextZ(): number {
  const models = currentModels()
  let max = -1
  for (const model of models) {
    if (typeof model.z === 'number' && Number.isFinite(model.z)) max = Math.max(max, model.z)
  }
  if (max + 1 < Z_LIMIT) return max + 1
  /* 重排：按大小重编号（相对次序不变） */
  const ordered = models
    .filter((model) => typeof model.z === 'number')
    .sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
  ordered.forEach((model, index) => {
    model.z = index
  })
  return ordered.length
}

/**
 * ★★ **把节点提到最上面**（用户 2027 要的那条）。
 *
 * > 节点的层级，选中节点应该默认到最顶，**不需要再回到原来的层级**。
 *
 * 所以这不是"选中时临时抬一下"，而是**改文档里的一个数**（`model.z`）——
 * 取消选中之后它还在上面。
 *
 * ⚠️ **不走 `commit()`**：这是选中的副作用，点一下记一步历史的话，
 * 撤销栈会被"我刚才点了几个节点"塞满（和拖动期间的就地写位置同一个理由）。
 * 它照样会被自动保存写进文档（那是 `useDocuments` 的事）。
 *
 * ⚠️ **框（`render: 'frame'`）不抬**：框是容器，抬到最上面会盖住里面的
 * 节点（它半透明、指针穿透，视觉上仍然很乱）。框的次序交给创建顺序。
 */
function raiseNode(nodeId: string): void {
  const model = modelOf(nodeId)
  if (!model || model.render === 'frame') return
  model.z = nextZ()
}

/**
 * **清空画布**。
 *
 * ⚠️ 它原来是「重置演示图」—— 重建那张硬编码的示例。
 * 示例搬走之后，唯一诚实的语义就是「清空」：把图交回空白状态。
 * 菜单里的文案也跟着改了（danger，需确认）。
 */
function resetGraph() {
  nodes.value = [] as unknown as Node[]
  edges.value = [] as unknown as Edge[]
  gesture.value = initialGestureState()
  clearNotice()
  nextTick(() => updateAllNodeInternals())
}
/**
 * 「排列 → 尺寸」：等宽 / 等高。
 *
 * ⚠️ 和 `doAlign` 分开：对齐改的是 `position`，这里改的是 `width`/`height`，
 * 而且尺寸的**真值在模型上**（`model.width`），不是 flow 节点的字段。
 * 所以它不能复用 `alignNodes` 那条路。
 */
function doResize(mode: SizeMode) {
  const ids = selectedIds.value
  if (!canResize(ids.length, mode)) return
  commit(mode === 'hsize' ? '等宽' : '等高')
  const boxes = ids.map(nodeBoxOf).filter((b): b is AlignBox => b !== null)
  for (const item of resizeNodes(boxes, mode)) {
    const model = modelOf(item.id)
    if (!model) continue
    if (mode === 'hsize') model.width = Math.max(MIN_NODE_WIDTH, item.width)
    /* 只有框 / 注释有显式高度；普通节点高度是内容撑的，写了也不生效 */
    else if (model.render === 'frame' || model.render === 'note') model.height = item.height
  }
  nextTick(() => updateAllNodeInternals(ids))
}

function doAlign(mode: AlignMode) {
  const ids = selectedIds.value;
  if (!canAlign(ids.length, mode)) return;
  commit("对齐 / 分布");
  const boxes = ids.map(nodeBoxOf).filter((b) => b !== null);
  const moved = new Map(alignNodes(boxes, mode).map((p) => [p.id, p]));
  for (const node of nodes.value) {
    const target = moved.get(node.id);
    if (target) node.position = { x: target.x, y: target.y };
  }
  nextTick(() => updateAllNodeInternals());
}
/* ── 视图操作（工具栏下段）─────────────────────────────────────
 *
 * ⚠️ 这四个**不改数据**，只改「怎么看」—— 所以它们不进历史、不 commit。
 * 和上段的「工具（模式）」、中段的「命令」是三件不同的事，
 * 分工见 `src/graph/shortcuts.ts` 的文件头注释。
 */
function zoomIn() {
  flowStore.value?.zoomIn?.({ duration: 150 })
}

function zoomOut() {
  flowStore.value?.zoomOut?.({ duration: 150 })
}

function fitView() {
  flowStore.value?.fitView?.({ padding: 0.2, duration: 200 })
}

/* ── 活动节点与选中（逻辑在 composables/useSelection.ts）────────── */

const {
  selectedEdgeIds,
  selectedIds,
  selectedIdSet,
  hasSelection,
  activeModel,
  onNodeClick,
  onPaneClick,
} = useSelection({
  flowStore,
  activeNodeId,
  /* 都用闭包：这几个声明在这段之后 */
  nodeViews: () => nodeViews(),
  modelOf: (id) => modelOf(id),
  /* ★ 点一下就把它提到最上面（用户 2027 要的"选中就到最顶，不再掉回去"） */
  raiseNode: (id) => raiseNode(id),
})

/* ── 快捷键 ─────────────────────────────────────────────────── */

/* ── 快捷键用到的三个动作（逻辑在 composables/useShortcutActions.ts）── */

const { targetNodesForToggle, toggleHideUnused, closePanels } = useShortcutActions({
  selectedIds,
  activeNodeId,
  /* ⚠️ socketTip 声明得比这段晚（端口浮层那段）—— 用 getter/setter 桥接 */
  socketTip: {
    get value() {
      return socketTip.value
    },
    set value(next) {
      socketTip.value = next as never
    },
  },
  flowStore,
  modelOf,
  commit,
  showNotice,
})

/**
 * 快捷键注册表。
 *
 * 底部提示条直接渲染它 —— 所以**说明和实现只有一份**，
 * 不会出现「文档写了但实际不生效」。
 * 部分条目只展示（`run` 为空），比如框选/平移，那些由图引擎处理。
 */
const { shortcuts } = useShortcuts({
  selectedIds,
  hasSelection,
  activeNodeId,
  /* ⚠️ 下面这几个声明在这段之后 —— 一律闭包 / getter 桥接（老规矩） */
  socketTip: {
    get value() {
      return socketTip.value
    },
    /*
     * ⚠️ setter 不能省：`closePanels` 会写 `socketTip.value = null`。
     * 只写 getter 的话，第一次关面板就抛
     * `Cannot set property value of #<Object> which has only a getter`。
     */
    set value(next) {
      socketTip.value = next as never
    },
  },
  levelPath,
  lassoMode,
  historyState,
  clipboardState,
  doUndo,
  doRedo,
  doCopy,
  doPaste,
  groupSelection: () => groupSelection(),
  ungroupSelection: () => ungroupSelection(),
  modelOf: (id) => modelOf(id),
  selectAll: () => selectAll(),
  toggleHideUnused,
  closePanels,
  targetNodesForToggle,
  deleteSelectedNodes: (mode) => deleteSelectedNodes(mode),
  exitSubgraph,
  openAddMenu,
  /* ── 工具栏专属命令的动作（键盘上没有对应键）── */
  doAlign,
  canAlign,
  /* ⚠️ 闭包桥接：`wrapSelectionInFrame` 来自 useGrouping，声明在这段**之后** */
  wrapSelectionInFrame: () => wrapSelectionInFrame(),
  zoomIn,
  zoomOut,
  fitView,
  /*
   * `Alt + 1..9` 切窗口类型：**作用于焦点窗口**（和点那个窗口头部的菜单同一条路）。
   * ⚠️ 收的是"焦点 id 此刻是多少"，所以用闭包读 `focusedId`，不吃声明顺序。
   */
  setFocusedEditor: (editor) => setEditor(focusedId.value, editor),
})

/* ── 快捷键的监听（逻辑在 composables/useHotkeys.ts）────────────── */

/*
 * ⚠️ `touchDevice` **先声明、后赋值**：`useMiniMapGesture`（上面）要用它，
 * 而它来自这个 `useHotkeys` 的解构。所以这里用一个 `let` 占位、
 * 下面把真 ref 赋进去 —— 上面那个 getter `() => touchDeviceRef.value`
 * 是**读取那一刻**才求值的，拿到的是赋值之后的真 ref。
 * （本仓库处理声明顺序的老规矩：要么 getter，要么「先声明后填充」。）
 */
let touchDeviceRef = ref(false)
const { touchDevice, visibleShortcuts, onSpaceKey } = useHotkeys({
  shortcuts,
})

/* 把真 ref 接到上面那个占位上（`useMiniMapGesture` 通过 getter 读它） */
touchDeviceRef = touchDevice

/* ── 手机档（「一次一个 Area」）─────────────────────────────────
 *
 * `docs/layout-redesign.md` §7.2 的三档 shell：
 *   桌面   自由分屏         （touchMode = false）
 *   手机   一次一个 + 模式栏（touchMode = true）
 *
 * ⚠️ 判据是 `touchDevice`，也就是 `(hover: none) and (pointer: coarse)`
 * ——**真·触屏设备**。触屏笔记本有鼠标，仍然走桌面档的分屏。
 */
/*
 * ⚠️ 这里原来有 `activeEditor` / `presentEditors` / `selectMobileEditor`
 * —— 手机档底部**模式栏**的数据侧。整块删除（组件也删了）：
 * 用户实测后要求「去掉移动端底部的按钮，让用户自己用多窗口切换」，
 * 改成**预设布局**（见 `src/layout/presets.ts` + 「窗口」菜单的布局一节）。
 */

/*
 * 进入触摸档时，把当前 Area 最大化（从桌面档的分屏切回「一次一个」）。
 * ⚠️ 只在**进入**那一刻做，之后不再干预 —— 否则用户手动还原来分屏时
 * 会被立刻again最大化，界面会「弹」回去。
 */
/*
 * ⚠️ 这里原来有一个 `watch(touchDevice, …)`：进触摸档就
 * `setOneAtATime(true)` + 整屏。
 *
 * **删掉了** —— 用户实测后要求手机上也支持多窗口。现在触摸档和桌面档
 * 在**布局行为上完全一致**：开窗口就是分屏，想整屏自己点 Area header
 * 上的最大化按钮。`touchDevice` 只剩两个用途（都不是布局）：
 *   · 底部提示条显示快捷键还是手势；
 *   · 输出窗口是贴底还是贴右（见 CSS）。
 */


/* ── 左侧工具栏的数据（全部**派生**，不手写第二份）────────────────
 *
 * ⚠️ `toolbarCommands` / `viewCommands` 都从 `shortcuts` 注册表过滤出来：
 *   · label  → `short`
 *   · 灰不灰 → `enabled()`
 *   · 图标  → `toolbar.icon`
 *   · 在哪段 → `toolbar.section`（context / view）
 * 所以工具栏和「编辑 / 视图」菜单**不可能**出现「菜单说不可用、按钮却能点」
 * 这种漂移 —— 它们读的是同一个函数。
 */
/*
 * ⚠️ 两段都从注册表的 `toolbar.section` 派生，**不按 id 前缀猜** ——
 * 第一版用 id 列表去分，结果 view 那 4 条同时出现在中段和下段
 * （中段的过滤条件是「有 toolbarIcon」，而它们也有），
 * 按钮总数从 18 涨到 22。结构化字段之后这种「同时属于两段」写不出来。
 */



/** 一次编辑会话只记一步历史（否则每敲一个字都进历史） */
let renameCommitted = false

function onRenameActive(value: string) {
  const model = activeModel.value
  if (!model) return
  if (!renameCommitted) {
    commit('改名称')
    renameCommitted = true
  }
  const trimmed = value.trim()
  model.label = trimmed === '' ? undefined : trimmed
}

/* ==========================================================================
   ④ 求值 + 视图（宿主负责把图算成值、把值画出来）
   ========================================================================== */

/**
 * 图或数据一变就整图重算。
 *
 * 真实场景应该做节点级缓存（key = 节点参数 + 上游输出 hash），
 * 演示规模下整图重算足够快，先不上缓存，避免过早复杂化。
 */
/**
 * 正在拖节点。
 *
 * ★ 拖拽中**不重算**图。
 *
 * 拖动只改位置，而位置**不影响求值结果** —— 但 `v-model:nodes` 每帧都会
 * 换掉数组，于是 `evalModels` 失效 → `evaluateGraph` + `validateGraph`
 * 每帧各跑一遍。节点一多、拖得快，CPU 就上去了（真实反馈：+12%）。
 *
 * 做法是拖拽期间直接返回上一次的结果（computed 仍然会重跑，但只读缓存，
 * 代价接近零）；松手时置回 false，自然就重算一次。
 */
/** 上一次真的算出来的结果 —— 拖拽期间复用它 */
let lastEvalResult: ReturnType<typeof evaluateGraph> | null = null

const evalResult = computed(() => {
  /*
   * ⚠️ **必须读一下版本号**：`BUILTIN_EVALUATORS` 是普通对象，
   * 装卸包时原地改它 —— 不读版本号的话这个 computed 不会重算，
   * 表现是「卸载了包但还在按旧求值器算」。
   */
  void packagesVersion.value
  if (draggingNodes.value && lastEvalResult) return lastEvalResult
  lastEvalResult = evaluateGraph(evalModels.value, evalEdges.value, {
    /* ⚠️ 数据源内容由宿主注入，内核不预置任何示例数据 */
    data: props.data ?? {},
    evaluators: BUILTIN_EVALUATORS,
  })
  return lastEvalResult
})

interface DebugRow {
  handle: string
  label: string
  kind: string
  display: string
  /** 这个值来自**上一次运行**（设计期没有值）—— 界面上要标出来 */
  fromRun: boolean
}

/**
 * **上一次运行的输出**（`nodeId → handle → 值`），id 归一化成**当前层的纯 id**。
 *
 * ⚠️ 为什么要归一化：子图里的节点在运行记录里是**带层级路径**的
 * （`group_x/const_y`），而画布上的 `p.id` 是当前层的纯 id ——
 * 不归一化的话，进到子图里看调试节点就永远是空的（同 `onLocateNode` 那个坑）。
 */
const lastRunOutputs = computed(() => {
  const normalized = new Map<string, ReadonlyMap<string, unknown>>()
  const record = runs.last.value
  if (!record) return normalized
  for (const [id, outputs] of record.outputs) {
    normalized.set(splitLevelId(id).nodeId, outputs)
  }
  return normalized
})

/**
 * ★★ **给包编辑器（页面编辑器）看的输出表**。
 *
 * 用户报的问题：
 *
 * > 示例文档，有一个掷幸运数字，但是我点运行，这个数字没变，是怎么回事
 *
 * 那份示例里幸运数字是 JS 节点在**运行时**掷的，而页面编辑器读的是
 * **组件节点的输出对象** —— 它是组件求值器在设计期算的（那时 JS 的值还不存在，
 * 于是回退到内联默认值）。所以光把"运行输出"并进表里**不够**：
 * 组件对象本身得**带着运行值重算一遍**。
 *
 * 做法就是拿 `evaluateGraph` 的 `seeds`（循环灌 `item` 用的那个口子）
 * 把副作用节点的值灌进去，让下游纯节点重新算 —— 见 `seedNodesFromRun`。
 *
 * ⚠️ 没有运行结果时**直接复用** `evalResult`：这一层不能给每一次改动
 * 都加一遍全图求值（拖节点、改参数都会触发它）。
 */
const docOutputs = computed(() => {
  const seeds = seedNodesFromRun(evalModels.value, lastRunOutputs.value, isEffectNodeType)
  if (Object.keys(seeds).length === 0) return evalResult.value.outputs
  return evaluateGraph(evalModels.value, evalEdges.value, {
    data: props.data ?? {},
    evaluators: BUILTIN_EVALUATORS,
    seeds,
  }).outputs
})

/** 这个类型是不是"副作用节点"（有执行器 —— JS / 事件那类，设计期不执行） */
function isEffectNodeType(type: string): boolean {
  return Boolean(BUILTIN_RUNNERS[type])
}

/**
 * 调试节点「监听」行当前收到的值。
 *
 * ## ★★ 两个来源，**设计期优先**（2027 补，用户报的问题）
 *
 * 用户原话：
 *
 * > 我给 JS 代码节点添加了一个输入数值，添加了一个输入布尔，然后输出连接到
 * > 调试输出节点上没有任何输出，也没有任何报错
 *
 * 原因是这个节点原来**只看设计期的求值结果**，而 JS 节点（以及事件这类
 * 副作用节点）设计期**不执行**（"打开文档 ≠ 执行文档"，那条规矩不能破）——
 * 于是把它们的输出接到这里，永远是空的。
 *
 * 现在：设计期有值就用设计期（纯节点，改了图立刻能看到）；设计期没值就
 * **回落到上一次运行的结果**，并标上「上次运行」。这样"接线 → 按运行 → 看结果"
 * 这条最自然的用法才走得通。
 *
 * ⚠️ "先看哪个来源"那套规矩在 `node/debugView.ts`（纯函数，有单测）——
 * 这里只负责把它变成能画的东西。
 */
function debugRows(nodeId: string): DebugRow[] {
  const row = modelOf(nodeId)?.rows.find((r) => r.kind === 'repeater') as
    | RepeaterRow
    | undefined
  if (!row) return []
  return debugValuesOf(row, evalResult.value.outputs.get(nodeId), lastRunOutputs.value.get(nodeId)).map(
    (item, index) => ({
      handle: item.handle,
      label: `#${index + 1}`,
      kind: valueKind(item.value),
      display: formatValue(item.value),
      fromRun: item.fromRun,
    }),
  )
}

/**
 * 「监听」行上**接线了吗**（不是"有值吗"）。
 *
 * 用来把空状态分成两句不同的话 —— 这个区别值钱：
 *   · 没接线     → "把任意端口连到「监听」上"（教怎么用）
 *   · 接了但没值 → "按一下运行"（副作用节点设计期就是没有值，
 *                  不说的话用户会以为接线坏了 —— 那正是他报的那件事）
 */
function debugHasLink(nodeId: string): boolean {
  const row = modelOf(nodeId)?.rows.find((r) => r.kind === 'repeater')
  return Boolean(row?.kind === 'repeater' && row.connected?.some(Boolean))
}

/** 空数组常量 —— 避免模板里每次生成新数组导致子组件无谓重渲染 */
const NO_DIAGNOSTICS: NodeDiagnostic[] = []


/**
 * 模板模板校验（必填未连接 / 值域越界）。
 *
 * 它和求值诊断**共用同一套展示通道** —— header 徽标、面板详情，
 * 不需要为「校验」再造一个 UI。
 *
 * 两者分工：
 *   求值层 → 算不算得出来（未注册求值器 / 抛异常 / 成环）
 *   校验层 → 算出来能不能真的用（空值 / 下游不接受的值）
 */
let lastValidation: ReturnType<typeof validateGraph> | null = null

const validationIssues = computed(() => {
  // 同 evalResult：拖拽期间复用上一次（校验依赖求值结果，重跑没有意义）
  if (draggingNodes.value && lastValidation) return lastValidation
  lastValidation = validateGraph({
    models: evalModels.value,
    edges: evalEdges.value as unknown as ValidateEdge[],
    readValue: (nodeId, handleId) =>
      evalResult.value.outputs.get(nodeId)?.get(handleId),
  })
  return lastValidation
})

const allDiagnostics = computed(() => [
  ...evalResult.value.diagnostics,
  ...validationIssues.value,
])

/* ── 输出窗口（逻辑在 composables/useProblems.ts）─────────────── */

/**
 * **打开输出窗口**（包点了它自己的感叹号徽标时调）。
 *
 * 规则（三条，按优先级）：
 *   ① 已经有一块**非本窗口**的窗口 → 把它切成输出（不新增窗口）；
 *   ② 没有别的窗口 → 把当前窗口**分屏**，新开的那块切成输出
 *      （这样"点感叹号"不会把用户正在编辑的页面顶掉）；
 *   ③ 输出窗口已经开着 → 只是把它**展开**（`logCollapsed = false`）。
 *
 * ⚠️ 包只发意图（"我想让用户看到详情"），怎么摆窗口是宿主的事 ——
 * 包不知道布局里有几块窗口，也不该知道。
 */
function openOutputWindow(): void {
  logCollapsed.value = false
  const current = focusedId.value
  /* ⚠️ 宿主这边的窗口列表来自 `useAreas`（`areas` 是当前布局的叶子） */
  const others = (layout.value ? allAreasOf(layout.value.root) : []).filter((a) => a.id !== current)
  const existing = others.find((a) => editorOf(a) === 'console')
  if (existing) {
    focusAreaById(existing.id)
    return
  }
  if (others.length > 0) {
    setEditor(others[0]!.id, 'console')
    focusAreaById(others[0]!.id)
    return
  }
  /* 只有这一块窗口：分屏出一块来放输出 */
  const before = new Set(areaIds.value)
  if (current) split(current, 'row')
  const fresh = areaIds.value.find((id) => !before.has(id))
  if (fresh) {
    setEditor(fresh, 'console')
    focusAreaById(fresh)
  }
}

const { logCollapsed, problems, diagnosticsByNode } = useProblems({
  /* 都用闭包注入：这几个声明在这段之后 */
  evalResult: () => evalResult.value,
  validationIssues: () => validationIssues.value,
  lastNotice: () => lastNotice.value,
  /* ★ 包报上来的那一批（`publishEditorProblems`）也并进来 —— 统一在输出窗口看 */
  packageProblems: () =>
    Object.values(editorDiagnostics.value)
      .flat()
      .map((item) => ({ severity: item.severity, message: item.message, ...(item.nodeId ? { nodeId: item.nodeId } : {}) })),
})

/* ── 端口悬停浮层（数据在 composables/useSocketTip.ts；指针事件留在下面） ── */

const { socketTip, socketValueText } = useSocketTip({
  /* 都用闭包：这三个声明在这段之后 */
  evalResult: () => evalResult.value,
  edgeViews: () => edgeViews(),
  modelOf: (id) => modelOf(id),
})

/** 指针进入端口 → 起浮层；进入别处 → 关掉 */
function onCanvasPointerOver(ev: PointerEvent) {
  // 连线拖拽中不显示，免得和连线的视觉打架
  if (linkingType.value) {
    socketTip.value = null
    return
  }

  const handle = (ev.target as HTMLElement | null)?.closest?.(
    `.${FLOW_DOM.handle}`,
  ) as HTMLElement | null
  if (!handle) {
    socketTip.value = null
    return
  }

  // Vue Flow 的 Handle 自带这两个 data 属性，不用自己去 DOM 上找
  const nodeId = handle.dataset.nodeid
  const handleId = handle.dataset.handleid
  const def = nodeId && handleId ? findSocket(nodeId, handleId) : undefined
  if (!nodeId || !handleId || !def) {
    socketTip.value = null
    return
  }

  socketTip.value = {
    name: handleId,
    type: def.itemType ? `${def.type}<${def.itemType}>` : def.type,
    value: socketValueText(nodeId, handleId),
    x: ev.clientX,
    y: ev.clientY,
  }
}

/** 浮层跟着鼠标走 */
function onCanvasPointerMove(ev: PointerEvent) {
  // 触摸长按：手指一移动就取消（在拖节点 / 平移，不是长按）
  if (ev.pointerType === 'touch') onTouchLongPressMove(ev)

  // ★ 先记位置，再管 tooltip —— 不能因为没 tooltip 就提前 return，
  //   那样 Shift+A 会永远拿不到鼠标位置，退回视口中心。
  lastCanvasPoint = { x: ev.clientX, y: ev.clientY }

  if (!socketTip.value) return
  socketTip.value.x = ev.clientX
  socketTip.value.y = ev.clientY
}

/* ==========================================================================
   ⑤ 其它：其它接线 + 视图杂项
   ========================================================================== */

/* ── 变参行拖拽排序（逻辑在 composables/useRepeaterReorder.ts）──── */

const { onRepeaterReorder } = useRepeaterReorder({
  edges: edges as unknown as RepeaterReorderCtx['edges'],
  flowStore,
  modelOf,
  commit,
  showNotice,
})

/* ── 控件编辑回调 + 预览（逻辑在 composables/useWidgetEditing.ts）── */

const {
  onWidgetResize,
  onToggleHideUnused,
  onToggleMute,
  componentPreview,
  sourcePreview,
} = useWidgetEditing({
  flowStore,
  /* ⚠️ evalResult 声明得比这段晚 —— 只在回调里读，闭包注入 */
  evalResult: () => evalResult.value,
  modelOf,
  commit,
  /*
   * 行高。原来是「实时调参」面板的旋钮值 `t.rowHeight`；
   * 面板删掉之后**唯一真值就是主题令牌** `--np-row-h` ——
   * 从 DOM 读，就不会再有「面板里的值」和「CSS 里的值」两份来源。
   */
  rowHeight: () => {
    const host = graphRef.value
    if (!host) return 22
    const raw = globalThis.getComputedStyle?.(host).getPropertyValue('--np-row-h')
    const value = Number.parseFloat(raw ?? '')
    return Number.isFinite(value) && value > 0 ? value : 22
  },
})

const validTargetCount = computed(() => {
  if (!linkingNodeId.value || !linkingHandleId.value) return 0
  let n = 0
  for (const node of nodeViews()) {
    if (node.id === linkingNodeId.value) continue
    n += (connectableFor(node.id) ?? []).length
  }
  return n
})

const statusText = computed(() => {
  if (notice.value) return notice.value
  const first = allDiagnostics.value[0]
  if (first) return `⚠ ${first.message}`
  if (!linkingType.value) {
    return `${nodes.value.length} 个节点 · ${edges.value.length} 条连线 · 点分组标题可折叠`
  }
  const where = linkingSide.value === 'target' ? '个输出可接' : '个端口可接'
  return validTargetCount.value > 0
    ? `拖线中：${linkingType.value} · ${validTargetCount.value} ${where}`
    : `拖线中：${linkingType.value} · 无可接端口（类型不符 / 会成环 / 已经连过）`
})

const patternColor = useThemeVar('--np-canvas-grid', 'rgba(255, 255, 255, 0.11)')

/* ── 外部可定制的信息 ───────────────────────────────────────── */

/**
 * 编辑器名称 / 版本，以及模板自己的元信息。
 *
 * ⚠️ 都做成 props 而不是写死 —— 这个编辑器要被**多个项目集成**，
 * 每个项目想显示的名字、版本、模板 ID 都不一样。
 * 演示页给的是默认值，集成方覆盖即可。
 */
const props = withDefaults(
  defineProps<{
    /** 左上角显示的编辑器名称 */
    editorName?: string
    editorVersion?: string
    /*
     * ── 文档元信息（**只在「属性」窗口显示**；配置行已删）──────────
     *
     * ⚠️ 这几个原来叫 `templateName` / `templateId` / `templateSize`，
     * 默认值是**假的示例**（`顺丰面单` / `tpl_sf_100x180` / `100 × 180 mm`）。
     * 那既是不该长在内核里的领域说法，也是个示例。
     *
     * 现在中性化成「文档」，而且**不提供默认值**：没传就不显示那一段
     * （见模板里的 `v-if`）。内核不该编造文档的身份 ——
     * 名称/ID/尺寸都是宿主的事。
     */
    documentName?: string
    /** 文档唯一 ID —— 创建后**不可更改**（外部系统靠它对齐） */
    documentId?: string
    documentSize?: string
    /**
     * 文档的数据源内容：`{ [sourceId]: { [fieldId]: value } }`。
     *
     * ⚠️ 这也是从内核里搬出来的示例（原来叫 `SAMPLE_DATA`，
     * 里面写死了顺丰单号 / 重量 / 颜色 / 条目）。数据源节点求值时要读它，
     * 所以它是**集成方的输入**而不是内核的常量。
     */
    data?: Record<string, Record<string, unknown>>
  }>(),
  {
    editorName: '节点设计器',
    editorVersion: 'v0.1.0',
  },
)

/*
 * 保存 / 导出用文档名当文件名。⚠️ 没有名字时给一个中性兜底 ——
 * 文件名不能是 `undefined.json`。
 *
 * ⚠️ 2027 起这个名字**来自文档库**（`useDocuments.currentName`）：
 * 宿主 props 只提供**初始身份**，之后用户在「文档」窗口里改的名字要
 * 立刻反映到标题、属性面板和保存的文件名上。所以 `documentName` 那个
 * 计算值是"库里的名字 → props 的名字 → 未命名文档"这条链。
 */
const documentLabel = computed(() => currentDocumentName.value)
const saveTemplate = () => saveTemplateFile(documentLabel.value, currentDocumentId.value ?? '')
const exportDocument = () => downloadDocument(documentLabel.value, currentDocumentId.value ?? '')

/* ══════════════════════════════════════════════════════════════════
   **文档库**（「文档」窗口 / 首页）

   模型：一份画布随时可能"还不属于任何文档"（刚打开时就是这样）——
   点「新建文档」或「保存到文档库」之后它才有身份，之后每次改动
   （防抖）自动存回库里那一条。细节见 `composables/useDocuments.ts`。
   ══════════════════════════════════════════════════════════════════ */

/**
 * **问一句**（覆盖当前画布 / 删掉一份文档 / 打开一份文件）—— **画面弹窗**。
 *
 * ## 为什么不用 `window.confirm`（用户实测报的）
 *
 * 用户原话：
 *
 * > 还有打开示例文档的时候，有一个弹窗，为什么不是画面弹窗而是系统弹窗？
 *
 * 第一版就是 `globalThis.confirm` —— 在浏览器里它是**系统模态**：样式和这个
 * 界面完全不是一回事，而且会**卡住整个页面**（渲染、动画、HMR 全停）。
 * 真正的理由更简单：这个仓库已经有一套自己的弹窗（`NpInfoDialog`，
 * 遮罩 / Esc / 焦点 / 滚动都验过），没有任何理由为了"问一句"去用系统的。
 *
 * ## 形状：`Promise<boolean>`
 *
 * 问一句天然是异步的（等人点），所以它返回 Promise —— 这让调用点必须
 * `await`，而不是"顺手 `if (!ask(...)) return`"（那种写法配上异步弹窗
 * 会**先把事做了再问**）。返回 `false` 的两个出口：点取消 / 按 Esc / 点遮罩。
 *
 * ⚠️ 同时只允许一个问题：新的问题会把上一个**当作取消**回掉
 * （`resolveConfirm(false)`），否则旧的那个 Promise 永远不落地 —— 那是
 * 一类"点了没反应"的幽灵（调用点还挂着 `await`，而弹窗已经换成新的了）。
 */
const confirmState = ref<{ message: string; resolve: (value: boolean) => void } | null>(null)

function askConfirm(message: string): Promise<boolean> {
  /* 上一个还没答就又问 → 旧的按"取消"结账（见上面那条注释） */
  confirmState.value?.resolve(false)
  return new Promise<boolean>((resolve) => {
    confirmState.value = { message, resolve }
  })
}

/** 回答当前这个问题（`null` = 没问题） */
function answerConfirm(value: boolean): void {
  const current = confirmState.value
  confirmState.value = null
  current?.resolve(value)
}

const {
  ready: documentsReady,
  storeKind: documentStoreKind,
  documents: libraryDocuments,
  currentId: currentDocumentId,
  currentName: currentDocumentName,
  dirty: documentDirty,
  summary: documentSummary,
  pendingImport: pendingDocumentImport,
  create: createDocumentRecord,
  open: openDocumentRecord,
  save: saveDocumentRecord,
  rename: renameDocumentRecord,
  remove: removeDocumentRecord,
  duplicate: duplicateDocumentRecord,
  exportFile: exportDocumentRecord,
  importText: importDocumentText,
  openExample: openDocumentExampleFlow,
  resolveImport: resolveDocumentImport,
} = useDocuments({
  initial: { id: props.documentId, name: props.documentName },
  nodes: nodes as unknown as { value: unknown },
  edges: edges as unknown as { value: unknown },
  currentFile,
  applyFile,
  clearGraph: resetGraph,
  /* ⚠️ 新建文档也换了一份文档 → 历史同样要清（`clearGraph` 自己不清：
     "清空画布"是**可以撤销**的，那条承诺不能破） */
  resetHistory: clearHistory,
  /**
   * ★★ **一份文档被装进画布之后：让用户看见它。**
   *
   * 默认布局是**只有文档窗口**（用户定的），所以"打开一份文档"必须自己
   * 解决"去哪儿看"这个问题：
   *
   * ```
   *   已经有画布窗口 → 把焦点交给它（焦点留在列表上的话，键盘快捷键
   *                    全打在文档窗口上：按 Shift+A 加节点没反应）
   *   没有画布窗口   → 把**当前这个**窗口换成节点图（用户刚点的那份文档，
   *                    就该在这里出现）—— 这是 `open` 那一路
   *   `restore`（刷新时自动打开上次那份）**不换窗口**：那时用户还没表态，
   *                    把首页换成画布等于把"默认窗口"这件事取消了
   * ```
   *
   * 要回文档列表：文件 → 文档管理…（没有就分屏开一个）。
   */
  onOpened: (reason) => {
    if (reason === 'open') showDocument()
  },
  showNotice,
})

/**
 * ★★ **把"当前这份文档"摆到眼前**。
 *
 * 默认布局是**只有文档窗口**（用户定的），所以"打开一份文档"必须自己解决
 * "去哪儿看"：有画布窗口就把焦点交给它，没有就把**当前这个**窗口换成节点图。
 *
 * ⚠️ 两处都用它，而且必须是同一个：`onOpened('open')`（刚 open/import 完）
 * 和「打开」当前那一份（那颗按钮以前是灰的 —— 用户实测："打开按钮是灰色的，
 * 打不开文档"）。两处各写一遍的话，"点卡片"和"点打开"的手感会不一样。
 *
 * ⚠️ `'restore'`（刷新时自动打开上次那份）**不走这里**：那时用户还没表态，
 * 把首页换成画布等于把"默认只显示文档窗口"这件事取消了。
 */
function showDocument(): void {
  /*
   * ⚠️ 判据必须是 `findAreaByEditor(...)`，**不能**用 `graphAreaId`：
   * 后者在找不到节点图时会**回落到第一个 Area**（那个 computed 是给
   * "旧窗口开关"用的兼容视图）—— 拿它判的话"没有画布窗口"这件事
   * 永远为假，于是下面那条兜底永远不执行（实测就是这样：点了打开，
   * 窗口还是文档列表，看着像"打开没反应"）。
   */
  const graph = findAreaByEditor(layout.value.root, 'graph')
  if (graph) {
    focusAreaById(graph.id)
    return
  }
  const current = focusedId.value
  if (current) setEditor(current, 'graph')
}

/** 「新建文档」：库里加一条空文档，画布跟着清空 */
async function createDocument(): Promise<void> {
  const ok = await askConfirm(
    '新建文档会把当前画布清空（当前这份如果没有存进库里，改动会丢）。继续吗？',
  )
  if (!ok) return
  const record = createDocumentRecord()
  showNotice(`已新建文档：${record.name}`)
}

/**
 * 「打开」库里的一份：会替换当前画布，先问一句。
 *
 * ★★ **当前正开着的那一份不是"重新打开"**（用户实测报的：那颗按钮灰着、
 * 点不动）。重新打开会把画布上**没存进库的改动**丢掉，而用户点它的意思
 * 通常是"把这份文档摆到眼前"（他多半在看文档库，画布在别的窗口里）。
 * 所以当前那一份走 `showDocument()`：和"打开一份文档之后"同一个动作 ——
 * 有画布窗口就聚焦它，没有就把当前窗口换成节点图。
 */
async function openDocument(id: string): Promise<void> {
  const record = libraryDocuments.value.find((item) => item.id === id)
  if (!record) return
  if (id === currentDocumentId.value) {
    showDocument()
    return
  }
  const ok = await askConfirm(
    `打开「${record.name}」会替换当前画布上的内容。\n没保存的改动会丢。继续吗？`,
  )
  if (!ok) return
  openDocumentRecord(id)
}

/** 「保存到文档库」：把当前画布写进库里那一条（没有身份就先建一份） */
function saveDocument(): void {
  const result = saveDocumentRecord()
  if (result.ok) showNotice(`已保存：${currentDocumentName.value}`)
}

/**
 * **打开 / 导入一份文件**（文件菜单与文档窗口共用这一条）。
 *
 * 顺序（每一步都有理由）：
 *   ① **先解析**再问 —— 文件根本不是我们的格式时，不该先弹一句
 *      "会替换当前画布"，然后再报"这不是模板文件"；
 *   ② 问一句"会替换当前画布"（和打开库里那一条同一条规矩）；
 *   ③ 进库 + 打开；**UUID 撞车时由弹窗裁决**（`pendingDocumentImport`）。
 */
function openDocumentFile(): void {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'application/json,.json'
  input.style.display = 'none'
  input.addEventListener('change', () => {
    const file = input.files?.[0]
    input.remove()
    if (!file) return
    void file
      .text()
      .then((text) => {
        const parsed = parseTemplateFile(text)
        if (!parsed.ok || !parsed.file) {
          showNotice(`打开失败：${parsed.error ?? '未知原因'}`)
          return
        }
        const label = parsed.file.name || '这份文件'
        void askConfirm(`打开「${label}」会把当前画布换掉。\n没保存的改动会丢。继续吗？`).then(
          (ok) => {
            if (!ok) return
                const outcome = importDocumentText(text)
            if (!outcome.ok) {
              showNotice(`打开失败：${outcome.error ?? '未知原因'}`)
              return
            }
            /* 撞车时 `pendingDocumentImport` 已经立起来了，宿主那边的弹窗会问 */
            if (outcome.conflict) showNotice('这份文件的 UUID 库里已经有了 —— 选一下怎么处理')
          },
        )
      })
      .catch(() => showNotice('打开失败：读不到文件内容'))
  })
  input.click()
}

/** 「导出」：把库里某一份下载成 .json（带 UUID，导回来认得出是同一份） */
function exportDocumentRecordFile(id: string): void {
  const record = libraryDocuments.value.find((item) => item.id === id)
  const file = exportDocumentRecord(id)
  if (!record || !file) return
  downloadFile(record.name, record.id)
}

/**
 * ★★ **把一份示例文档变成"一份文件"**（「文件 → 示例文件」走这条）。
 *
 * 示例在包里是**模型**（`PackageExample.graph()` 给的是 `SubGraph`：
 * 节点是 `GraphNodeModel`），而进文档库要的是**序列化后的** `GraphDocument`。
 * 这一层转换就是那一步 —— 顺手把 `pkg`（"这个节点来自哪个包"）写进去，
 * 于是缺包时有话可说。
 *
 * ⚠️ 每一步都复用现成的实现（`makeFlowNode` / `toDocument` /
 * `buildTemplateFile`）：示例文档和用户自己的文档走**同一条**入库路，
 * 它没有任何特殊待遇 —— 包括那个 UUID 撞车弹窗。
 */
function documentFromExample(example: PackageExample): TemplateFile {
  const graph = example.graph()
  const views = graph.nodes.map((model) =>
    makeFlowNode(model, graph.positions?.[model.id] ?? { x: 0, y: 0 }),
  )
  const document = toDocument(
    views as unknown as Parameters<typeof toDocument>[0],
    graph.edges as unknown as Parameters<typeof toDocument>[1],
    { packageOf: nodePackageOf },
  )
  return buildTemplateFile(document, toolSettings, { id: example.uuid, name: example.name })
}

/** 打开一份示例（菜单里点的那一下）—— 走的是和打开文件**完全一样**的那条路 */
function openExampleDocument(id: string): void {
  const example = exampleOf(id)
  if (!example) {
    showNotice('这个示例不在目录里了（贡献它的包被卸载了？）')
    return
  }
  if (!askConfirm(`打开示例「${example.name}」会把当前画布换掉。\n没保存的改动会丢。继续吗？`)) return
  const outcome = openDocumentExampleFlow(example, () => documentFromExample(example))
  if (!outcome.ok) {
    showNotice(`打开示例失败：${outcome.error ?? '未知原因'}`)
    return
  }
  /* 撞车时弹窗会问（示例的 UUID 是固定的：第二次打开就会问"覆盖吗"） */
  if (outcome.conflict) showNotice('这个示例库里已经有一份了 —— 选一下怎么处理')
}

/** 「示例文件」子菜单的清单（包一带上示例，菜单里就出现） */
const documentExamples = computed<PackageExample[]>(() => {
  /* ⚠️ 读一下版本号：装卸包之后示例清单要跟着变（见 `packagesVersion`） */
  void packagesVersion.value
  return [...EXAMPLES]
})

/** 冲突弹窗：取消 */
function cancelDocumentImport(): void {
  if (pendingDocumentImport.value) pendingDocumentImport.value = null
}

/**
 * 打开「文档」窗口（没有就分屏开一个）—— 同 `openOutputWindow` 那套。
 */
function openDocumentsWindow(): void {
  const current = focusedId.value
  const others = (layout.value ? allAreasOf(layout.value.root) : []).filter((a) => a.id !== current)
  const existing = others.find((a) => editorOf(a) === 'documents')
  if (existing) {
    focusAreaById(existing.id)
    return
  }
  if (others.length > 0) {
    setEditor(others[0]!.id, 'documents')
    focusAreaById(others[0]!.id)
    return
  }
  const before = new Set(areaIds.value)
  if (current) split(current, 'row')
  const fresh = areaIds.value.find((id) => !before.has(id))
  if (fresh) {
    setEditor(fresh, 'documents')
    focusAreaById(fresh)
  }
}

/**
 * 菜单里那两行「文档名 / 文档 ID」要**跟着库里的当前文档变**。
 *
 * ⚠️ 菜单那些 section 是在 computed 里读 `ctx.props` 的，所以喂给它一个
 * **reactive 对象**（不是渲染时抓一次的快照）—— 否则在文档窗口里改完名字，
 * 菜单里还是旧的那个。
 */
const documentMenuProps = reactive({ documentId: '', documentName: '' })
watch(
  [currentDocumentId, currentDocumentName],
  () => {
    documentMenuProps.documentId = currentDocumentId.value ?? ''
    documentMenuProps.documentName = currentDocumentName.value
  },
  { immediate: true },
)

/* ── 菜单分发（逻辑在 composables/useMenus.ts）────────────────── */

/* ── 用选中节点建组 + 框 / 拖拽（逻辑在 composables/useGrouping.ts）── */

const {
  groupSelection,
  ungroupSelection,
  wrapSelectionInFrame,
  assignFrameOf,
  onNodeDragStart,
  onNodeDrag,
  onNodeDragStop,
} = useGrouping({
  nodes: nodes as unknown as GroupingCtx['nodes'],
  edges: edges as unknown as GroupingCtx['edges'],
  flowStore,
  graphRef,
  draggingNodes,
  gesture,
  selectedIds,
  edgeViews,
  modelOf,
  findSocket,
  nodeBoxOf,
  nodePosition,
  looseNode,
  applyNodePositions,
  frameBoxes,
  childrenOfFrame,
  nextNodeId,
  makeModelOfType,
  makeFlowNode,
  /* `makeEdge` 现在要显式拿端口类型（纯函数）——包一层适配接线类型 */
  makeEdge: (source, sourceHandle, target, targetHandle) =>
    makeEdge(
      (nodeId, handleId) => socketTypeOf(nodeId, handleId),
      source,
      sourceHandle,
      target,
      targetHandle,
    ),
  commit,
  showNotice,
})

const {
  contextSections,
  barSections,
  MENUS,
  openBarMenu,
  menuSections,
  onMenuSelect,
  closeMenu,
  eventPoint,
  edgeMuted,
  onNodeMenu,
  projectOntoEdge,
} = useMenus({
  menu,
  contextEdgeId,
  /* `menuPicked` 是 ref，两边读写同一个盒子（见它的声明处的注释） */
  menuPicked,
  nodes: nodes as unknown as MenusCtx['nodes'],
  edges: edges as unknown as MenusCtx['edges'],
  flowStore,
  graphRef,
  activeNodeId,
  selectedIds,
  edgeViews,
  currentModels,
  modelOf,
  sampleEdgePathFine,
  openAddMenu,
  selectedIdSet,
  /* ⚠️ 喂**reactive 对象**而不是 `props`：菜单里那两行要跟着文档库改名而变 */
  props: documentMenuProps,
  documentDirty,
  openDocumentsWindow,
  saveDocument,
  openDocumentFile,
  examples: documentExamples,
  openExample: openExampleDocument,
  addSections,
  linkCandidateSections,
  toFlowPoint,
  insertRerouteOnEdge: (edge, at, ev) => insertRerouteOnEdge(edge as never, at, ev),
  putEdgeBack,
  historyState,
  clipboardState,
  windows,
  toggleWindow,
  workspaces,
  activeWorkspaceId,
  applyWorkspace,
  saveWorkspace,
  deleteWorkspace,
  /* ── Area 菜单（右键区域头）── */
  layout,
  swapAreas: (a: string, b: string) => {
    layout.value = swapAreas(layout.value, a, b)
  },
  toggleMaximize: onToggleMaximize,
  closeArea: (id: string) => closeAreaById(id),
  toggleRegionOf: (id: string, key: RegionKey) => toggleRegion(id, key),
  splitAreaOf: (id: string, dir: 'row' | 'col') => split(id, dir),
  setEditorOf: (id: string, editor: EditorType) => setEditor(id, editor),
  setPanelOfSide: (id: string, side: 'left' | 'right', tab: string | null) => setPanelOfSide(id, side, tab),
  /*
   * 「系统 → 关于」。
   * ⚠️ 用 `showNotice` 而不是新开一个弹窗：这里要报的是
   * 「版本 / 主题 / 工作区 / 窗口数」这几个**当前状态**，
   * 一条带撤销位的提示条就够，而弹窗要额外管焦点、关闭、遮罩三件事。
   */
  openAbout: () => openInfo('about'),
  openHelp: () => openInfo('help'),
  setTheme: setActiveTheme,
  themes: themeSummaries,
  themeId: activeThemeId,
  themeSummary: activeThemeSummary.value,
  /*
   * ⚠️ 「打开主题编辑器」= **开一个窗口**，和输出窗口同一个机制。
   * 原来是打开一个浮层 —— 浮层的关闭不影响布局，窗口则会记住
   * 「它开着」，存进工作区之后下次还能原样回来。
   */
  toggleThemeWindow: () => toggleWindow('theme'),
  commit,
  doUndo,
  doRedo,
  doCopy,
  doPaste,
  /* 剪切 = 复制 + 删除；没有独立的实现，见 useMenus 里的组合 */
  doCut: () => {
    doCopy()
    deleteSelectedNodes()
  },
  doAlign,
  doResize,
  canAlign,
  canResize,
  updateNodeInternals: updateAllNodeInternals,
  toggleHideUnused,
  deleteSelectedNodes: (mode?: 'frame-only' | 'with-children') => deleteSelectedNodes(mode),
  selectAll: () => selectAll(),
  groupSelection,
  ungroupSelection,
  wrapSelectionInFrame,
  closePanels,
  exportRenderTree,
  exportDocument,
  saveTemplateFile: saveTemplate,
  /* ── 节点包（装卸在 usePackages，这里只把接口转给菜单）── */
  installedPackages,
  assetSections,
  /* `openAssetMenu` 来自 useAddNode 的解构（和 `openAddMenu` 同一处） */
  openAssetMenu,
  importPackage,
  uninstallPackage,
  enablePackage,
  exportPackage,
  showNotice,
  resetGraph,
})

/* ── 节点编辑回调（逻辑在 composables/useNodeEditing.ts）────────── */

const {
  onBoxResize,
  onRenameNode,
  onRepeaterAdd,
  onRenameRepeater,
  onRowToggle,
  onNoteUpdate,
  onEdgeDoubleClick,
  insertRerouteOnEdge,
  onPaneMenu,
  onSelectionMenu,
  onEdgeMenu,
  onGraphDoubleClick,
  deleteSelectedNodes,
  /* 文档通道的 `removeNodes` 用它 —— 见 `useNodeEditing` 的返回处注释 */
  deleteNodesById,
  selectAll,
} = useNodeEditing({
  nodes: nodes as unknown as NodeEditingCtx['nodes'],
  edges: edges as unknown as NodeEditingCtx['edges'],
  flowStore,
  graphRef,
  activeNodeId,
  selectedIds,
  menu,
  contextEdgeId,
  edgeViews,
  modelOf,
  looseNode,
  frameBoxes,
  childrenOfFrame,
  isStructurallyValid,
  edgeMuted,
  projectOntoEdge,
  toFlowPoint,
  eventPoint,
  focusNode,
  openAddMenu,
  isProtectedNode,
  refreshInterfaceOf,
  nextNodeId,
  makeModelOfType,
  makeFlowNode,
  /* `makeEdge` 现在要显式拿端口类型（纯函数）——包一层适配接线类型 */
  makeEdge: (source, sourceHandle, target, targetHandle) =>
    makeEdge(
      (nodeId, handleId) => socketTypeOf(nodeId, handleId),
      source,
      sourceHandle,
      target,
      targetHandle,
    ),
  doUndo,
  commit,
  showNotice,
})

/* ══════════════════════════════════════════════════════════════════
   **文档通道** —— 宿主给包贡献的编辑器的读写口
   （逻辑在 `composables/useEditorDoc.ts`）。

   ⚠️ 它必须放在**这一段之后**：写入口要转调宿主已有的那条路
   （`commit` 历史 / `applyConnection` 连线 / `deleteNodesById` 级联删除），
   而那些都声明在本文件更靠前的位置 —— 依赖方向由声明顺序保证，
   和别处一样（见文件头那三层）。
   ══════════════════════════════════════════════════════════════════ */
/**
 * *包报上来的问题*（`EditorProblem`）—— 按来源整批替换。
 *
 * ⚠️ 和 `useProblems` 同一个取向：**派生**，不是追加。改好了就该消失。
 * 里面存的是"每个窗口最近一次报上来的那一批"，换窗口/关窗口时由包自己
 * 报空（或者它的最后一批随窗口一起被忘掉 —— 见 `openOutputWindow` 旁边的说明）。
 */
const editorDiagnostics = ref<Record<string, readonly EditorDiagnostic[]>>({})

function publishEditorDiagnostics(senderId: string, problems: readonly EditorDiagnostic[]): void {
  const next = { ...editorDiagnostics.value }
  if (problems.length === 0) delete next[senderId]
  else next[senderId] = problems
  editorDiagnostics.value = next
}

const { doc: editorDoc } = useEditorDoc({
  nodes: nodes as unknown as EditorDocCtx['nodes'],
  edges: edges as unknown as EditorDocCtx['edges'],
  selection: () => selectedIds.value,
  activeNodeId,
  /*
   * ★★ 包编辑器（打印包的页面编辑器）读的输出 = **设计期 ∪ 上次运行**
   * （设计期优先，见 `node/debugView.ts` 的 `mergeOutputs`）。
   *
   * ⚠️ 用户报的问题就是这个：示例文档里"掷幸运数字"是 JS 节点在**运行时**
   * 才出的值，而这里原来只给设计期那一份 —— 于是点运行之后纸上的数字
   * 一动不动（预览看到的一直是下游的内联默认值）。
   *
   * ⚠️ 不合并的话还有一个更难查的形态：**运行结果里别的层级的 id 是带路径的**
   * （`group_x/const_y`），`lastRunOutputs` 已经归一化过了，直接用那一份。
   */
  evalOutputs: () => docOutputs.value,
  pkgOf: nodePackageOf,
  commit,
  modelOf,
  nextNodeId,
  makeModelOfType,
  makeFlowNode,
  applyConnection,
  isStructurallyValid,
  deleteNodes: deleteNodesById,
  viewCenter: () => toFlowPoint(viewportCenter()),
  /*
   * ⚠️ 就地改文档之后必须**换掉那个节点的引用**：节点模型是普通对象，
   * 就地改字段不会触发响应式 —— 不 republish 的话求值与文档投影都不重算，
   * 表现是"面板读不到自己刚写的值"（真机反馈的"改不了拉伸"就是这个）。
   */
  republishNode: (nodeId: string) => {
    nodes.value = republish(nodes.value as unknown as { id: string }[], [nodeId]) as unknown as Node[]
  },
})
/*
 * ── 视口复位守卫 ─────────────────────────────────────────────────
 *
 * ⚠️ 移动端（尤其 iOS 横屏）有个坑：**聚焦输入框时浏览器会把页面往上滚**，
 * 键盘收起后它**不一定滚回来** —— 表现就是「顶栏不见了，只剩下面的内容」。
 * 我们的菜单搜索框、注释正文都是输入框，所以这条必定会遇到。
 *
 * 所以：视口尺寸一变（键盘收起 / 旋转屏幕）就把滚动位置复位。
 * 只动 `scrollTo`，不碰任何布局 —— 副作用可控。
 */
function resetViewportScroll() {
  if (typeof window === 'undefined') return
  if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0)
}

onMounted(() => {
  window.visualViewport?.addEventListener('resize', resetViewportScroll)
  window.addEventListener('orientationchange', resetViewportScroll)
  // 输入框失焦后也复位一次（键盘刚收起那一刻）
  window.addEventListener('focusout', resetViewportScroll)
})

onBeforeUnmount(() => {
  window.visualViewport?.removeEventListener('resize', resetViewportScroll)
  window.removeEventListener('orientationchange', resetViewportScroll)
  window.removeEventListener('focusout', resetViewportScroll)
})

</script>

<template>
  <!--
    ⚠️ `np-designer` 这个类**不能省**：
    整套 `--np-panel-*` 令牌（面板背景 / 边框 / 文字 / 滚动条 / 徽标底色）
    定义在 `.np-designer` 上，只在浅色主题下才由 `.np-theme-light` 兜一份。
    深色下少了它 → 输出窗口的 `background: var(--np-panel-bg)` 是**无效声明**
    → 面板透明（用户看到的「输出没有背景」）。加上它，令牌继承下来即可。
  -->
  <!--
    ⚠️ **右键菜单：窗口区域内一律不弹浏览器的**（真机反馈："窗口中右键会弹出
    浏览器的默认菜单。能不能给全局所有的窗口区域，不要让它右键弹出浏览器的
    默认菜单？"）。

    ⚠️ **可编辑控件是唯一的例外**（`input` / `textarea` / `contenteditable`）：
    那里的右键菜单是"粘贴 / 全选"的入口，挡掉它是真的丢功能 ——
    而用户抱怨的是画布 / 标题栏 / 面板上那些"另存为 / 检查 / 翻译"。
    判定写在 `suppressNativeContextMenu()` 里（纯函数，可断言）。
  -->
  <div
    ref="designerEl"
    class="pg np-designer"
    :class="{ 'np-theme-light': light }"
    :style="cssVars"
    @contextmenu="onContextMenu"
  >
    <PlaygroundHeader
      v-if="chromeHeaderVisible"
      :menus="MENUS"
      :editor-name="props.editorName"
      :version="props.editorVersion"
      :notice="notice"
      :actions="noticeActions"
      :workspaces="workspaces"
      :active-workspace-id="activeWorkspaceId"
      @open-menu="openBarMenu"
      @run-action="(a) => runNoticeAction(a as never)"
      @pick-workspace="onPickWorkspace"
      @save-workspace="onSaveWorkspace"
      @remove-workspace="onRemoveWorkspace"
    />

    <!--
      ══════════════════════════════════════════════════════════════
      Area 树 —— 整个工作区（画布 / 输出 / 将来的版面、大纲、属性…）

      ⚠️ 这里是「布局即数据」的渲染端：树长什么样，界面就长什么样。
      加一个窗口 = 在树里加一个 Area，不再需要新的 CSS 定位规则。

      ⚠️ 插槽内容只写一次，由 `NpAreaTree` 递归透传到每个 Area 的
      `NpArea` 上；「这个 Area 放什么」的判断留在宿主（这里），
      因为状态在宿主。
      ══════════════════════════════════════════════════════════════
    -->
    <!--
      ⚠️ `@pointerover` 挂在**树的容器**上，靠 `closest('.np-area')` 反查 ——
      而不是让每个 `NpArea` 往外 emit 一个 hover 事件。
      后者要在递归的 `NpAreaTree` 里多透传一个事件，而它对中间层毫无意义。
      事件本来就是冒泡的，用它。
    -->
    <NpAreaTree
      @pointerover="onAreaPointerOver"
      :node="layout.root"
      :maximized-id="maximizedId"
      :focused-id="focusedId"
      :area-count="areaIds.length"
      :scroll-marks="areaScrolls"
      @resize="setRatio"
      @focus="focusAreaById"
      @set-editor="setEditor"
      @toggle-region="toggleRegion"
      @set-panel="setPanelOfSide"
      @split="split"
      @split-at="splitAt"
      @join="joinAreaById"
      @close="closeAreaById"
      @toggle-maximize="onToggleMaximize"
      @area-menu="onAreaMenu"
      @editor-menu="onEditorMenu"
      @panel-toggled="onPanelToggled"
      @scroll="rememberScroll"
    >
      <!--
        ══════════════════════════════════════════════════════════════
        窗口自己的 header 内容（`#headerExtra`）。
        ⚠️ 这里**取代了左侧工具栏**。工具栏原来三段（工具模式 / 上下文命令 /
        视图操作），实测反馈：「去掉工具栏，放大缩小不需要了，只保留一个
        手按钮和缩放合适的按钮，手默认激活」。

        所以只剩两个**视图操作**，而且它们是**这个窗口的**操作 ——
        分屏之后每个窗口各有一份，不会互相打架。
        其余命令本来就有手势 / 快捷键 / 菜单三条入口，工具栏只是冗余路径。

        ⚠️ （这里原来还有一个「手」模式按钮，已随模式概念一起删除）
        初始为 false —— 也就是触摸端单指拖动 = 平移画布。
        ══════════════════════════════════════════════════════════════
      -->
      <template #headerExtra="{ area }">
        <!--
          ⚠️ **包贡献的编辑器**可以带一个 header 组件（`EditorMeta.header`）：
          宿主只摆位置、不认识它画什么。这一条让"窗口里的按钮归包"成立 ——
          否则宿主就得为每个包写一段 `v-if`（那是 B 路）。
        -->
        <component
          :is="headerComponentOf(editorOf(area))"
          v-if="headerComponentOf(editorOf(area))"
          :area="area"
          :doc="editorDoc"
        />
        <template v-if="editorOf(area) === 'graph'">
          <!--
            ══════════════════════════════════════════════════════════
            ★★ **运行**（这一步让"工具箱"在界面上成立）

            · 图里有「手动触发」节点 → 只跑它们的下游闭包（事件驱动语义）
            · 一个都没有 → 跑整张图
            · 正在跑时点它 = **取消**（图标变方块，title 写明）
            · 能力不足时**灰着**，title 里说清缺什么（不是点了才报错）
            ══════════════════════════════════════════════════════════
          -->
          <button
            class="np-area__icon"
            type="button"
            data-np-run
            :data-np-run-state="runs.phase.value"
            :title="runTitle"
            :disabled="!runs.gate.value.ok && runs.phase.value !== 'running'"
            @click="runManual"
          >
            <svg
              v-if="runs.phase.value !== 'running'"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M7 4.5v15l12-7.5z" />
            </svg>
            <svg
              v-else
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
          <!--
            ⚠️ header 上**只放「手」一个模式**（另外那个「框选」在左侧
            工具面板里）。理由是实测反馈：「一排小按钮只有适应画面应该是
            按钮，而不是有选中状态的按钮」—— 也就是说这一排里应当只有
            **一个**带选中态的按钮，其余是普通动作按钮。
          -->
          <!--
            ⚠️ 这里原来有一个**「手」切换按钮**（一个按钮两个模式：亮 = 移动、
            灭 = 框选）。**删掉了** —— 它存在的唯一理由是"单指只能做一件事"，
            而那个冲突现在没有了：

              · 触摸端：单指 = 框选，**双指 = 平移 + 缩放**（`useCanvasPinch`）
              · 桌面端：左键拖动 = 平移，**Shift + 拖动 = 框选**

            模式是个**桌面概念**（鼠标按钮不够分才需要它），搬到手机上就变成
            "移动端还得先按个开关"。冲突消失之后，开关本身就没有存在理由了。
          -->
          <button
            class="np-area__icon pg__viewbtn"
            type="button"
            title="适配缩放（把整张图缩放到刚好放得下）"
            @click="fitView()"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4" />
              <circle cx="12" cy="12" r="2.2" />
            </svg>
          </button>
        </template>
      </template>

      <!--
        ══════════════════════════════════════════════════════════════
        侧栏面板（Blender 的 N / T 面板）。

        ⚠️ 内容按 `(editor, side, tab)` 分派 —— `NpArea` 只负责壳和竖排
        tab 条，**它不认识"视图选项"是什么**。哪些 tab 存在由
        `EDITOR_META.panels` 声明，所以没有 tab 的编辑器连开关都不出现。
        ══════════════════════════════════════════════════════════════
      -->
      <template #panel="{ area, side, tab }">
        <!--
          ⚠️ **包贡献的面板**由包自己渲染（tab 上带着组件）。
          宿主不认识它，能做的只有"把它放进侧栏"——
          所以这一支必须在最前面：`EDITOR_META.panels` 里凡是带
          `component` 的 tab，内容都不归宿主分派。
          ⚠️ 外面套 `NpBoundary`：包的面板抛异常时只坏这一块，
          不能把整个窗口带走（内核自己的面板不套 —— 它们在同一份测试里）。
        -->
        <NpBoundary
          v-if="packagePanelOf(editorOf(area), side, tab)"
          :label="`${EDITOR_META[editorOf(area)].label} · 侧栏面板`"
          :source="EDITOR_META[editorOf(area)].from"
        >
          <component
            :is="packagePanelOf(editorOf(area), side, tab) as never"
            :area="area"
            :doc="editorDoc"
          />
        </NpBoundary>

        <!--
          ⚠️ 这里原来有一页左侧「工具」。**删掉了** ——
          工具模式已经在 header 的手按钮上（一个按钮两个模式），
          左侧再放一份只是同一件事说两遍。
          `EDITOR_META.graph.panels` 里现在没有 `left`，所以
          **左侧那个面板按钮也不会出现**（空面板不给按钮）。
        -->

        <template v-else-if="editorOf(area) === 'graph' && side === 'right'">
          <!--
            ⚠️ 这里原来还有一页「视图」（列画布显示项）。**删掉了** ——
            那一页里唯一的一项就是小地图，而 header 上本来就有小地图开关。
            同一件事两个入口是纯冗余。
          -->

          <!--
            「属性」：和「属性」窗口**同一份内容**（同一个组件、同一批 props）。
            ⚠️ 是 `v-if` 而不是 `v-else-if` —— 上面那页删掉之后它就没有
            "相邻的 v-if" 了，模板编译会直接报
            「v-else/v-else-if has no adjacent v-if」。
            （`vue-tsc` **不查模板语法**，只有 `verify:sfc` 和真实构建会红。）
          -->
          <PlaygroundProperties
            v-if="tab === 'properties'"
            :document-name="currentDocumentName"
            :document-id="currentDocumentId ?? ''"
            :document-size="props.documentSize"
            :selected-ids="selectedIds"
            :active-node-id="activeNodeId"
            :model-of="modelOf"
            :set-value="setNodeRowValue"
            :edit-version="nodeEditVersion"
          />
        </template>
      </template>

      <!--
        ⚠️ 这里原来有一个 `#sidebar` 插槽（「实时调参」面板）。
        它删过一轮（用户：「去掉右侧浮窗和调参窗口，没啥用」），
        现在换成上面那个**通用的** `#panel` —— 侧栏不再是某一个面板的特例，
        而是窗口的基础能力（见 `EDITOR_META.panels`）。
      -->

      <!--
        编辑器主体：按 Area **显示的那个**编辑器分派。
        ⚠️ 一律走 `editorOf(area)`（当前 → 默认 → 节点图），不读 `area.editor`：
        后者是"这个窗口要显示什么"，可能指向一个**没装的包**贡献的类型。
        由它回落之后，这里的分派永远只面对注册表里认得的类型。
      -->
      <template #editor="{ area }">
        <!-- ── 节点图 ─────────────────────────────────────────── -->
        <template v-if="editorOf(area) === 'graph'">
              <!--
                ⚠️ 画布顶上这里原来有一条**配置行**（文档名 / ID / 尺寸的常驻横条，
                RegionKey 里的 `legend`）。它整个删掉了 —— 实测反馈
                「节点图窗口的配置行区域可以去掉了，没啥用处了」。

                同一批 props 在「属性」窗口里本来就有一份（`PlaygroundProperties`），
                挂一条常驻横条只是在画布顶上白占一行高度。
                `RegionKey` 里的 `legend` 和 `useAreas` 的 `windows.legend`
                也一并删了 —— 留一个没有消费者的开关比删掉更坏。
              -->

              <!--
                面包屑：进了子图必须随时知道「我在哪」并且能出去。
                没有它的话进去就等于迷路。
              -->
              <nav v-if="levelPath.length" class="pg__crumbs">
                <button class="pg__crumb" type="button" @click="exitSubgraph(-1)">顶层</button>
                <template v-for="(step, i) in levelPath" :key="step.nodeId">
                  <span class="pg__crumb-sep">›</span>
                  <button
                    class="pg__crumb"
                    type="button"
                    :class="{ 'is-current': i === levelPath.length - 1 }"
                    @click="exitSubgraph(i + 1)"
                  >
                    {{ step.title }}
                  </button>
                </template>
                <span class="pg__spacer" />
                <span class="pg__crumb-hint">双击节点主体进入 · Esc 退出</span>
                <button class="pg__btn" type="button" @click="exitSubgraph()">退出一层</button>
              </nav>

              <div
                :ref="(el) => bindGraphEl(area.id, el)"
                class="np-graph"
                :data-np-minimap-corner="minimapCorner"
                @pointerdown.capture="onCanvasPointerDownCapture"
                @pointermove.capture="canvasPinch.onPointerMove"
                @pointerdown="onTouchLongPressStart"
                @pointerup="onCanvasPointerUp"
                @pointercancel="onCanvasPointerCancel"
                @pointerover="onCanvasPointerOver"
                @dblclick="onGraphDoubleClick"
                @pointermove="onCanvasPointerMove"
                @pointerleave="onCanvasPointerLeave"
              >
      <!--
        ⚠️ 这段注释在 2027 换渲染层时重写过。

        **原来是**：框选和左键平移抢同一个键，于是要按设备算
        `selection-key-code` / `pan-on-drag` 两个 prop 的组合
        （引擎的判据是 `shouldSelectOnDrag = selectionKeyCode === true && shouldPanOnDrag !== true`）。

        **现在是**：我们自己的画布把这三件事拆开了，不需要再算组合 ——

        ```text
          左键拖空白处   → 框选（默认行为，任何设备一致）
          中键拖 / 空格拖 → 平移画布
          触摸单指        → 框选；双指交给 useCanvasPinch（平移 + 缩放）
        ```

        触摸端"单指框选、双指平移"这条正是当年的真机事故
        （「移动端不能手指拖动节点图」）：那时引擎的 d3-zoom 过滤器把整条
        触摸链挡掉了。现在手势全在我们自己手里，不再有那个过滤器。

        ⚠️ 小地图上的手势**归宿主**（`useMiniMapGesture`）：画布只把
        `minimap-pointer-*` 事件原样转出来，自己不缩放（重叠两套缩放手势
        正是当年"小地图完全拖不动"的原因）。这一段**漏接过一次** ——
        画布那边做了事件、这里没接，表现就是「小地图完全拖不动」（真机反馈），
        所以 `verify:canvas-dom` 里有一条断言盯着这条通路。

        ⚠️ 写模板注释只能用 HTML 注释标记。**JS 风格的块注释在模板里不算注释** ——
        Vue 会把里面的字当成属性解析，报一串"属性重复"，而且行号指到奇怪的地方。
      -->
      <NpCanvas
        :nodes="nodes"
        :edges="edges"
        @nodes-change="onNodesChange"
        @edges-change="onEdgesChange"
        :is-valid-connection="isValidConnection"
        :min-zoom="0.2"
        :max-zoom="3"
        :connection-radius="30"
        :default-viewport="{ x: 30, y: 20, zoom: 1 }"
        :minimap="area.regions.minimap && !isMinimapFolded(area.id)"
        :minimap-size="minimapSize"
        :minimap-corner="minimapCorner"
        :minimap-node-color="minimapNodeColor"
        :minimap-node-stroke="minimapNodeStroke"
        :minimap-mask-color="minimapMaskColor"
        :background-dot="patternColor"
        @minimap-pointer-down="(ev) => onMiniMapPointerDown(area.id, ev)"
        @minimap-pointer-move="(ev) => onMiniMapPointerMove(area.id, ev)"
        @minimap-pointer-up="onMiniMapPointerUp"
        @minimap-pointer-cancel="onMiniMapPointerUp"
        @init="(instance) => onFlowInit(area.id, instance)"
        @viewport-change="(vp) => rememberViewport(area.id, vp)"
        @connect="onConnect"
        @connect-start="onConnectStart"
        @connect-end="onConnectEnd"
        @node-click="onNodeClick"
        @pane-click="onPaneClick"
        @node-context-menu="onNodeMenu"
        @pane-context-menu="onPaneMenu"
        @selection-context-menu="onSelectionMenu"
        @edge-context-menu="onEdgeMenu"
        @edge-double-click="onEdgeDoubleClick"
        @node-drag-start="onNodeDragStart"
        @node-drag="onNodeDrag"
        @node-drag-stop="
          (params) => {
            onNodeDragStop(params)
            /* 拖动期间位置是就地写的，松手后收敛一次（见 publishFromStore） */
            publishFromStore(area.id)
          }
        "
      >
        <template #node-np="p">
          <NpFlowNode
            :model="dataModel(p.data)"
            :selected="selectedIdSet.has(p.id)"
            :active="activeNodeId === p.id"
            :collapsed="(p.data as NpNodeData).collapsed"
            :linking="linking"
            :connectable-sockets="connectableFor(p.id)"
            @update:collapsed="(v) => setCollapsed(p.id, v)"
            @group-toggle="
              (groupId, collapsed) => onGroupToggle(p.id, groupId, collapsed)
            "
            :resizable="p.data.model.render !== 'dot'"
            :has-subgraph="p.data.model.type === 'group' || p.data.model.type === 'loop'"
            :type-hint="nodeTypeOf(p.data.model.type)?.hint"
            :type-icon="nodeIconOf(p.data.model.type)"
            :actions="nodeTypeOf(p.data.model.type)?.actions"
            @node-action="(actionId) => onNodeAction(p.id, actionId)"
            :diagnostics="diagnosticsByNode.get(p.id) ?? NO_DIAGNOSTICS"
            :origin-socket="
              linkingNodeId === p.id ? linkingHandleId : null
            "
            @resize="(w) => onResize(p.id, w)"
            @toggle-mute="(m) => onToggleMute(p.id, m)"
            @toggle-hide-unused="onToggleHideUnused(p.id)"
            @box-resize="(w, h) => onBoxResize(p.id, w, h)"
            @note-update="(text) => onNoteUpdate(p.id, text)"
            @row-toggle="(rowId, value) => onRowToggle(p.id, rowId, value)"
            @rename="(label) => onRenameNode(p.id, label)"
            @row-rename="(rowId, index, name) => onRenameRepeater(p.id, rowId, index, name)"
            @repeater-add="(rowId) => onRepeaterAdd(p.id, rowId)"
            @enter-subgraph="enterSubgraph(p.id)"
            @repeater-reorder="
              (rowId, from, to) => onRepeaterReorder(p.id, rowId, from, to)
            "
            @widget-resize="
              (rowId, widgetId, height) =>
                onWidgetResize(p.id, rowId, widgetId, height)
            "
            @repeater-remove="
              (rowId, index) => onRepeaterRemove(p.id, rowId, index)
            "
            @widget-update="
              (rowId, widgetId, value) => {
                // 连续调参（拖滑块、拖数字）合并成一步历史
                commit('修改参数', 700)
                onWidgetUpdate(p.id, rowId, widgetId, value)
              }
            "
          >
            <template #row-display="{ row }">
              <!-- 调试查看器：逐条显示「监听」行收到的值 -->
              <div v-if="row.render === 'debug'" class="pg__debug">
                <div
                  v-for="item in debugRows(p.id)"
                  :key="item.handle"
                  class="pg__debug-row"
                >
                  <span class="pg__debug-kind">{{ item.kind }}</span>
                  <code class="pg__debug-value">{{ item.display }}</code>
                  <!--
                    ⚠️ 标出"这个值来自上一次运行"：设计期与运行期是两个时刻，
                    不标的话用户会以为它就是现在图上的值（改了图它会**不跟着变**）。
                  -->
                  <span v-if="item.fromRun" class="pg__debug-src" title="这个值来自上一次运行（设计期这类节点不执行）">
                    上次运行
                  </span>
                </div>
                <div v-if="!debugRows(p.id).length" class="pg__debug-empty">
                  <template v-if="debugHasLink(p.id)">
                    已连上，但还没有值：先按「运行」跑一次<br />
                    跑完还是空的，就检查上游的输入接上了没有
                  </template>
                  <template v-else>把任意端口连到「监听」上</template>
                </div>
              </div>

              <!-- 组件预览：用求值结果渲染 -->
              <div
                v-else-if="row.render === 'component'"
                class="pg__preview pg__preview--component"
              >
                <span class="pg__preview-text">{{ componentPreview(p.id) }}</span>
              </div>

              <!-- 数据源预览 -->
              <div v-else class="pg__preview">
                <span class="pg__preview-value">{{ sourcePreview(p.id) }}</span>
                <span class="pg__preview-hint">数据源 order</span>
              </div>
            </template>
          </NpFlowNode>
        </template>

        <!--
          ⚠️ 底纹**不再是一个子组件**：画布自己画（`:background-dot` 传色）。
          原来的 `<Background variant="dots" :gap="22" :size="1.4">` 是引擎的
          一个插件包，现在那三行样式在 `NpCanvasBackground.vue` 里
          （`radial-gradient` + `background-size`，零 DOM 节点）。
        -->

        <!--
          **空画布提示**。
          ⚠️ 它是因为「内核不再预置示例图」才需要的：初始图搬走之后
          首次打开是一片空白网格，不告诉人能干什么就等于没入口。
          两条出路正好对应两个真实的起点：从节点开始，或打开已有文档。
          `pointer-events: none` 让点击穿透到画布 —— 空状态下
          画布本身还能框选 / 平移，提示不该挡住。
        -->
        <div v-if="nodes.length === 0" class="pg__empty">
          <p class="pg__empty-title">空白画布</p>
          <p class="pg__empty-hint">
            按 <kbd>Shift</kbd> + <kbd>A</kbd> 添加节点，或在画布上右键
          </p>
          <button class="pg__btn pg__empty-action" type="button" @click="openAddMenu()">
            添加第一个节点
          </button>
          <button class="pg__btn pg__empty-action" type="button" @click="openDocumentFile()">
            打开文档…
          </button>
        </div>
        <!--
          小地图：**画布自己渲染**（`:minimap` prop），宿主只提供配色与尺寸。
          节点按**类别配色**，扫一眼就能看出模板里各部分的比例。

          ⚠️ 尺寸用 **props** 传（画布同时写进 SVG 的 attribute 和 inline style）。
          原来是注释里记的那条坑：MiniMap 读 props 不读 CSS，于是 class 设成
          200×130 而 SVG 仍按 200×150 画 —— 右侧多一块，拖动换算也错。
          现在两者同一个来源，不会再分叉。
        -->
        <!--
          ⚠️ 小地图上的手势**归宿主**（下面三个 pointer 处理器 →
          `useMiniMapGesture`）：画布把事件原样转出来，自己不缩放。
          重叠两套缩放手势正是当年"小地图完全拖不动"的原因。
        -->
        <!--
          小地图的折叠开关。
          ⚠️ 它必须是画布的**兄弟节点**（走默认插槽），不能包一层：
          四个角的定位读的是 `.np-graph[data-np-minimap-corner]`
          （和画布内部的小地图读同一个值），套一层 div 会让它相对那个 div 定位。
        -->
        <button
          v-if="area.regions.minimap"
          class="pg__minimap-fold"
          :class="{ 'is-folded': isMinimapFolded(area.id) }"
          type="button"
          :title="isMinimapFolded(area.id) ? '展开小地图' : '收起小地图'"
          :aria-expanded="!isMinimapFolded(area.id)"
          @click="toggleMinimapFold(area.id)"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path v-if="isMinimapFolded(area.id)" d="M4 15l8-8 8 8" />
            <path v-else d="M4 9l8 8 8-8" />
          </svg>
        </button>
      </NpCanvas>
      <!--
        面板容器已经搬去 #sidebar Region；划痕必须留在 .np-graph 内部 ——
        放外面 `position: absolute` 会相对视口算，整条划痕会偏移掉
        顶栏的高度（表现为「线不跟着鼠标」）。
      -->
              <svg
                v-if="lassoMode || linkPreviewPath"
                class="pg__lasso"
                :class="lassoMode ? `is-${lassoMode}` : ''"
              >
                <polyline v-if="lassoMode" :points="lassoPoints" />
                <!-- 已摘下待接的那条线：从原端口到菜单弹出位置 -->
                <path v-if="linkPreviewPath" class="pg__link-preview" :d="linkPreviewPath" />
              </svg>
        </div>
        </template>

        <!-- ── 输出 ───────────────────────────────────────────── -->
        <NpLogPanel
          v-else-if="editorOf(area) === 'console'"
          :problems="problems"
          :runs="runs.history.value"
          :collapsed="logCollapsed"
          @toggle="logCollapsed = !logCollapsed"
          @focus-node="onLocateNode"
        />

        <!--
          ── 属性 ───────────────────────────────────────────────
          ⚠️ 为什么值得单独开一个编辑器，而不是在画布顶上挂一条常驻横条：
          那种横条只能放**速览**（一行 10.5px 的窄带，够放名称/ID/尺寸），
          放不下「选中了什么、它有哪些可编辑项」；而它如果越变越长，
          画布就会被顶掉 —— 手机上尤其明显。

          两者的数据源是同一份（模板 props + 选中 id），所以不会不一致。
          这就是 Blender 的分工：header 显示对象名，Properties 编辑器
          显示全部属性。
        -->
        <PlaygroundProperties
          v-else-if="editorOf(area) === 'properties'"
          :document-name="currentDocumentName"
          :document-id="currentDocumentId ?? ''"
          :document-size="props.documentSize"
          :selected-ids="selectedIds"
          :active-node-id="activeNodeId"
          :model-of="modelOf"
          :set-value="setNodeRowValue"
          :edit-version="nodeEditVersion"
        />

        <!--
          ── 主题编辑器 ──────────────────────────────────────────
          ⚠️ 它是**一个正常的工作区窗口**，不是浮层。
          做成浮层时它盖住的正是你要看的东西 —— 调节点外观却看不见节点，
          只能"关掉看一眼再打开"。做成窗口之后和画布并排，改一个值
          直接看到画布上的变化，而且能最大化 / 分屏 / 存进工作区。
        -->
        <NpThemeEditor v-else-if="editorOf(area) === 'theme'" />

        <!--
          ── 文档（文档库 / 也能当首页）──────────────────────────────
          用户要的那一页：列出库里所有文档、改名、打开、导入导出。
          ⚠️ 组件只收**数据 + 一组动作**（库里怎么存、UUID 怎么生成、
          打开时弹不弹窗，全在 `useDocuments`）—— 这条分工让它能被
          组件级单测直接挂起来验，不用起整个 Playground。
        -->
        <NpDocumentManager
          v-else-if="editorOf(area) === 'documents'"
          :ready="documentsReady"
          :store-kind="documentStoreKind"
          :documents="libraryDocuments"
          :current-id="currentDocumentId"
          :current-name="currentDocumentName"
          :dirty="documentDirty"
          :summary="documentSummary"
          :ask="askConfirm"
          :actions="{
            create: createDocument,
            open: openDocument,
            save: saveDocument,
            rename: renameDocumentRecord,
            duplicate: duplicateDocumentRecord,
            remove: removeDocumentRecord,
            exportFile: exportDocumentRecordFile,
            importFile: openDocumentFile,
          }"
        />

        <!--
          ── 包贡献的编辑器 ────────────────────────────────────────
          ⚠️ 它排在**最后**（在内核那几个 v-else-if 之后、在兜底之前）：
          包不许占用内核的类型标识（注册表抛错），所以两支不可能同时成立；
          排最后只是为了让内核那几条的分派读起来还是老样子。
          宿主交给它的只有 `{ area }` —— 窗口身份，不是宿主的内部状态。
          ⚠️ 外面套 `NpBoundary`：包的渲染错误只坏这一块（内核那几条不套）。
        -->
        <NpBoundary
          v-else-if="packageEditorOf(editorOf(area))"
          :label="EDITOR_META[editorOf(area)].label"
          :source="EDITOR_META[editorOf(area)].from"
        >
          <component
            :is="packageEditorOf(editorOf(area)) as never"
            :area="area"
            :doc="editorDoc"
            :publish-diagnostics="publishEditorDiagnostics"
            :open-output="openOutputWindow"
          />
        </NpBoundary>

        <!-- ── 还没实现的编辑器（兜底）─────────────────────────── -->
        <div v-else class="pg__stub">
          <strong>{{ editorOf(area) }}</strong>
          <span>这个编辑器还没实现。在它的头部下拉里换回「节点图」即可。</span>
        </div>
      </template>
    </NpAreaTree>

    <!--
      ══════════════════════════════════════════════════════════════════
      **全局底部提示条**（Blender 的 status bar）

      ⚠️ 它原来是**每个 Area 自己的**一个 Region。三个理由改成全局唯一：

        1. Blender 就是一根 —— 内容是「鼠标底下那个窗口」的提示；
        2. 分屏之后每个窗口各挂一条，会看到好几份一模一样的提示；
        3. 每条都占一份高度 —— 窗口越分越小，提示却越铺越多。

      ⚠️ 显示谁的提示：**指针悬停的窗口**优先，没有指针（触摸端）时用
      焦点窗口。这样"看着哪个窗口"和"提示说什么"永远对得上。
      ══════════════════════════════════════════════════════════════════
    -->
    <footer v-if="windows.keys" class="pg__statusbar">
      <div class="pg__statusbar-hints">
        <!--
          ⚠️ 节点图那一套是**命令注册表**（Ctrl+H / Delete / Ctrl+G…），
          只在图窗口里有意义。

          ⚠️ 别的窗口走**包自己声明的 `tips`**（`EditorMeta.tips`）——
          真机反馈："页面编辑器的快捷操作方式，我没有在快捷条里边看到有任何提示渲染"：
          滚轮缩放、拖动吸附、双指缩放这些操作在界面上原来**无处可查**。
          内核不认识那些操作，只负责把 `keys / label` 排成 `<kbd>`。
        -->
        <template v-if="hintArea && editorOf(hintArea) === 'graph'">
          <template v-for="s in visibleShortcuts" :key="s.id">
            <span v-if="shortcutLabel(s, touchDevice)" class="pg__key">
              <kbd>{{ shortcutLabel(s, touchDevice) }}</kbd>
              <span>{{ s.label }}</span>
            </span>
          </template>
        </template>
        <template v-else-if="hintArea">
          <template v-for="tip in visibleTips(editorOf(hintArea))" :key="tip.keys + tip.label">
            <span class="pg__key">
              <kbd>{{ tip.keys }}</kbd>
              <span>{{ tip.label }}</span>
            </span>
          </template>
        </template>
      </div>
      <!--
        ⚠️ 只留版本号。应用名**去掉了** —— 顶栏左上角本来就有它，
        而实测反馈是「右下角可以去掉标题名」。同一个名字在一屏里出现两次，
        第二次就是噪音。
      -->
      <div class="pg__statusbar-right">
        <span v-if="props.editorVersion" class="pg__statusbar-version">{{ props.editorVersion }}</span>
      </div>
    </footer>

    <!--
      ⚠️ 「系统 → 帮助 / 关于」的弹窗。
      内容按 `infoDialog` 分派，组件只管壳（遮罩 / Esc / 焦点 / 滚动）。
    -->
    <NpInfoDialog
      :open="infoDialog !== null"
      :title="infoDialog === 'help' ? '帮助' : '关于'"
      :hint="
        infoDialog === 'help'
          ? '快捷键与手势一览。鼠标、触屏都能用。'
          : '这个编辑器现在跑的是什么。'
      "
      @close="infoDialog = null"
    >
      <template v-if="infoDialog === 'help'">
        <section v-for="g in helpRows" :key="g.group" class="pg__help-group">
          <h3 class="pg__help-title">{{ g.group }}</h3>
          <dl class="pg__help-list">
            <template v-for="row in g.items" :key="row.keys">
              <dt class="pg__help-keys"><kbd>{{ row.keys }}</kbd></dt>
              <dd class="pg__help-what">{{ row.what }}</dd>
            </template>
          </dl>
        </section>
      </template>
      <template v-else>
        <dl class="pg__help-list">
          <template v-for="row in aboutRows" :key="row.label">
            <dt class="pg__help-keys">{{ row.label }}</dt>
            <dd class="pg__help-what">{{ row.value }}</dd>
          </template>
        </dl>
        <p class="pg__help-note">
          {{ ABOUT_INTRO }}
        </p>
        <!--
          ⚠️ 下面这几行**从注册表生成**：启用了哪些包、包里有哪些窗口，
          这里就跟着变 —— 换一个域包（图像 / 数据 / 以后的别的），
          「关于」不会再说自己是打印机。
        -->
        <dl class="pg__help-list">
          <dt class="pg__help-keys">已启用</dt>
          <dd class="pg__help-what">{{ enabledPackagesSummary }}</dd>
        </dl>
      </template>
    </NpInfoDialog>

    <!--
      ★★ **问一句**（画面弹窗，2027 改）。

      用户原话：

      > 还有打开示例文档的时候，有一个弹窗，为什么不是画面弹窗而是系统弹窗？

      第一版挂的是 `window.confirm`（系统模态：样式不是这个界面的，
      而且会卡住整个页面）。现在和 UUID 裁决、帮助/关于共用同一个壳
      （`NpInfoDialog`：遮罩 / Esc / 焦点 / 滚动都验过），
      两个出口就是"确定 / 取消"。

      ⚠️ 按钮带 `data-np-confirm-*`：端到端测试按它们点（不然只能靠文案找按钮，
      而文案是会改的）。
    -->
    <NpInfoDialog
      :open="confirmState !== null"
      title="确认一下"
      hint="这一步不能撤销，先把话说完"
      @close="answerConfirm(false)"
    >
      <p class="pg__help-note" data-np-confirm-message>{{ confirmState?.message }}</p>
      <div class="pg__doc-conflict">
        <button
          class="pg__doc-conflict-btn is-primary"
          type="button"
          data-np-confirm-ok
          @click="answerConfirm(true)"
        >
          继续
        </button>
        <button
          class="pg__doc-conflict-btn"
          type="button"
          data-np-confirm-cancel
          @click="answerConfirm(false)"
        >
          取消
        </button>
      </div>
    </NpInfoDialog>

    <!--
      ★★ **UUID 重复**的裁决弹窗（用户要的那一条）。
      「加载文档的时候，如果发现 UUID 有重复的时候弹窗提醒是否覆盖」——
      三个出口：覆盖库里那份 / 另存为新文档 / 取消。
      ⚠️ 复用 `NpInfoDialog` 的壳（遮罩 / Esc / 焦点 / 滚动都验过了），
      按钮放在它默认插槽里 —— 那个组件本来就只有"标题 + 内容 + 关闭"。
    -->
    <NpInfoDialog
      :open="pendingDocumentImport !== null"
      title="文档 UUID 重复"
      hint="这份文件里的文档已经在这个编辑器的库里了"
      @close="cancelDocumentImport"
    >
      <template v-if="pendingDocumentImport">
        <dl class="pg__help-list">
          <dt class="pg__help-keys">库里那份</dt>
          <dd class="pg__help-what">{{ pendingDocumentImport.existing.name }}</dd>
          <dt class="pg__help-keys">UUID</dt>
          <dd class="pg__help-what">
            <code class="pg__legend-id">{{ pendingDocumentImport.existing.id }}</code>
          </dd>
          <dt class="pg__help-keys">这份文件</dt>
          <dd class="pg__help-what">
            {{ pendingDocumentImport.file.name || '（没有名字）' }}
            · {{ pendingDocumentImport.file.document.nodes.length }} 节点
          </dd>
        </dl>
        <p class="pg__help-note">
          覆盖会把库里那份的内容换成文件里的（UUID 不变）；另存为会生成一个新的
          UUID，两份都留着。
        </p>
        <div class="pg__doc-conflict">
          <button
            class="pg__doc-conflict-btn is-primary"
            type="button"
            data-np-doc-overwrite
            @click="resolveDocumentImport('overwrite')"
          >
            覆盖库里那份
          </button>
          <button class="pg__doc-conflict-btn" type="button" data-np-doc-keep @click="resolveDocumentImport('keep')">
            另存为新文档
          </button>
          <button class="pg__doc-conflict-btn" type="button" data-np-doc-cancel @click="cancelDocumentImport">
            取消
          </button>
        </div>
      </template>
    </NpInfoDialog>

    <!--
      **触摸端的「添加节点」走全屏选择器**，不走下拉菜单。
      ⚠️ 判据是 `touchDevice`（`(hover: none) and (pointer: coarse)`）——
      下拉菜单是靠 hover 展开子菜单的控件，在没有悬停的设备上本来就是错的。
      两者吃的是**同一份 `menuSections`**，内容不会分叉。
    -->
    <NpNodePicker
      v-if="showNodePicker"
      :sections="menuSections"
      @pick="onMenuSelect"
      @close="closeMenu"
    />

    <!--
      ⚠️ 顶栏被主题藏起来时的**唯一入口**。
      没有它，「隐藏顶栏」这个主题就是单向门：菜单栏在顶栏里，
      藏起来之后用户再也点不到「视图 → 主题」把顶栏换回来。
    -->
    <button
      v-if="!chromeHeaderVisible"
      class="pg__unhide-bar"
      type="button"
      title="顶栏被当前主题隐藏了 —— 打开主题编辑器换一套"
      @click="toggleWindow('theme')"
    >
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <path d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>

    <!-- 右键菜单：模型见 menuSections，组件负责可滚动 + 键盘导航 -->
    <NpMenu
      v-if="menu && !showNodePicker"
      :sections="menuSections"
      :x="menu.x"
      :y="menu.y"
      :searchable="pickerKinds.includes(menu.kind) || menu.kind === 'link'"
      :search-placeholder="searchPlaceholder"
      @select="onMenuSelect"
      @close="closeMenu"
    />

    <!--
      底部快捷键提示条（参考 Blender 的状态栏）。
      内容直接来自快捷键注册表 —— 说明和实现只有一份。
    -->

    <!--
      端口悬停浮层。
      用 Teleport 挂到 body：否则会被 .np-graph 的 overflow 裁掉。
      内容 = 端口名 + 类型 + **当前值**。
    -->
    <Teleport to="body">
      <div
        v-if="socketTip"
        class="pg__tip"
        :style="{
          left: `${socketTip.x + 14}px`,
          top: `${socketTip.y + 18}px`,
        }"
      >
        <span class="pg__tip-name">{{ socketTip.name }}</span>
        <span class="pg__tip-type">{{ socketTip.type }}</span>
        <code class="pg__tip-value">{{ socketTip.value }}</code>
      </div>
    </Teleport>

  </div>
</template>

<!--
  ⚠️ 这里原来有一整块 scoped 样式（1072 行），**已抽到 `playground.css`**
  —— 它是本文件最大的一块，而且和逻辑完全无关。

  ⚠️ 抽成**普通 CSS 文件**（不再走 SFC 的样式块）是**故意的**：
  那份样式里**一个 `:deep()` 都没有**、选择器全是 `.pg__*` 这类
  自带前缀的类名 —— 也就是说 scoped 的属性选择器本来就没起作用。
  抽出去之后层叠顺序也没变（下面那个全局样式块仍然在它之后）。

  ⚠️ 写这段注释时踩了一次：**别在注释里写出真的标签名**（哪怕在
  `<!-- -->` 里）—— SFC 解析器会把它当成真的样式块，构建立刻失败
  （`[vite:vue] Unexpected '/'`）。用文字描述，别贴标签。
-->

<!--
  触摸行为 + Teleport 浮层的主题覆盖。

  ⚠️ **故意不加 scoped**：
    · `.np-graph__viewport` 是**画布组件内部**的元素，
      宿主 scoped 的 `[data-v-…]` 加不到它，规则会静默失效；
    · `.np-menu` 是 Teleport 到 body 的浮层，同理。
  选择器都带 `.np-graph` / `.np-menu` / `.pg` 前缀，不会外溢。
  `.np-log` 同属这一类（它是 NpLogPanel 的子组件根元素）。
-->
<style>
.np-graph,
.np-graph .np-graph__canvas,
.np-graph__viewport {
  touch-action: none;
}

/* ══════════════════════════════════════════════════════════════════
   输出窗口的位置
   · 网页端（鼠标）：贴**底部**，和画布上下分栏 —— 宽屏横向空间不值钱，
     纵向也够，贴底最符合「日志在下面」的直觉。
   · 移动端：改贴**右侧**当侧栏。手机纵向最紧张，底部那条会吃掉画布高度；
     而横向宽、又正好有空白。

   判据是 `(hover: none) and (pointer: coarse)` —— **真·触屏设备**。
   「手机上把日志放侧面」是个**形态**决定，不是「点击目标多大」的决定，
   所以不用 `(pointer: coarse)`（那个连触屏笔记本也算进来）。
   ══════════════════════════════════════════════════════════════════ */

/*
 * ══════════════════════════════════════════════════════════════════
 * ⚠️ 这里原来有一整块「移动端把输出窗口改成右侧浮层」的规则，**已删除**。
 *
 * 它做的是：`.pg .np-log { position: absolute; right: 8px; top: 8px;
 * bottom: calc(130px + 24px); width: min(300px, 54vw) }`，外加一条
 * `.pg__side--shifted { right: calc(min(300px,54vw) + 16px) }` 让调参面板躲开它。
 *
 * 为什么不做了：输出窗口现在是**一个独立的 Area**（见模板里 #editor 的
 * console 分支），有自己的位置、能被切分、能被最大化。
 *
 * 原来的做法有三个连带问题：
 *   ① `.np-log` 在桌面是 flex 兄弟（挤扁画布）、在移动端是浮层（盖住画布）——
 *      同一个组件两种布局语义，正是「移动端小地图不能操作」那类 bug 的温床；
 *   ② `bottom` 必须手算让开小地图（`130px + 24px`），
 *      于是「小地图多大」这个事实又多了一个抄本；
 *   ③ 输出窗口和左侧工具栏同时开着时，390px 的屏只剩约 36px 画布（§P3）。
 *
 * 现在这三条从结构上都不存在了。`NpLogPanel` 自己也不再需要知道
 * 「我在屏幕的哪一侧」—— 那是它父级 Area 的事。
 * ══════════════════════════════════════════════════════════════════ */

</style>